/**
 * The mechanics bench.
 *
 * A student builds an arrangement out of hooks, pulleys, weights, rope, ramps
 * and spring balances, reads back what the textbook asks about it — tension,
 * mechanical advantage, the constraint relation, the normal and friction
 * forces, what the ceiling carries — and then runs it. See
 * `docs/FeatureDesigns/mechanics-bench-target.md`.
 *
 * It began as a pulley bench and outgrew the name: a ramp with friction on it
 * and a block hanging from a spring are the same statics problem as a block
 * and tackle, and the engine never cared which one it was given.
 *
 * All of the physics is in `@padcanvas/shared`'s mechanics engine and none of
 * it is here: this file is gestures, layout and a cursor. Not even the clock —
 * the engine returns a RUN, a list of constant-acceleration phases with exact
 * times, so playing it is reading `at(t)` and the transport is a scrubber over
 * something already worked out. Nothing is integrated frame by frame, so what
 * the bench shows cannot depend on the frame rate, on how long a tab was
 * backgrounded, or on anything the student did not choose.
 *
 * Placing is a CLICK, not a drag from the rail. Dragging a part out of a list
 * is the gesture that fails first on a touch screen and the one a projector
 * audience cannot see; clicking drops the part on the bench and dragging then
 * moves it, which is one gesture fewer to teach.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { CloseButton, ThemeToggle } from '../components/PaneButtons';
import '../components/BenchType.css';
import { useBenchFullscreen } from '../components/useBenchFullscreen';
import { useBenchTouch } from '../components/useBenchTouch';
import { FullscreenIcon } from '../components/FullscreenIcon';
import { BenchPhoneHeader } from '../components/BenchPhoneHeader';
import type { mechanics as M, labSetups as L } from '@padcanvas/shared';
import { mechanics } from '@padcanvas/shared';
import { useLabSetups, SaveSetupDialog, type LabSaving } from '../lab-setups';
import { BENCH_CHAPTER_OPTIONS } from '../simulations/benchSetups';
import { MechanicsScene2D, portKey, readsUp, ROOM_ABOVE_CEILING, ROOM_BELOW_FLOOR, type SceneZoom } from './MechanicsScene2D';
import { PartIcon, type PartGlyph } from './PartIcons';
import { fixed, kg } from './format';
import './MechanicsBench.css';

const {
    PRESETS, openingBench, buildRun, at, worldAt, runDuration, diagnose, isBuildable,
    reading, joinPorts, fasten, fastening, canJoin, canTake, carriesFrom, place, placeRope, removePart, removeLink, bondsHolding,
    movePart, pullEndTo, setData, setRopeLength, shortestRope, windable, rest, surfacesFor, mintId, partById, defOf, ropesOf, slackOf,
    bodiesOf, bodyIndex, allKinds, isFree, segmentsOf, spanId, tensionOf, pin, anyPinned, released, retie,
} = mechanics;

type World = M.World;
type PortRef = M.PortRef;

/** The parts rail, in the order a bench is usually built.
 *
 *  No rope end: a loose end comes with the rope the Connector tile adds, and
 *  one left untied is the hand — holding, or pulling with a force set on it. */
const RAIL = ['anchor', 'sheave', 'weight', 'table', 'incline', 'springBalance'];

/** A phone held upright. Held sideways is a layout of its own, still to come. */
const PHONE_PORTRAIT = '(max-width: 640px) and (orientation: portrait)';

/** Whether the upright-phone layout is showing. The layout is CSS; this is for
 *  what the phone does differently — the view pinches and pans, a part's
 *  settings sit under its tile, a long press stands in for a double-click. */
function usePhonePortrait(): boolean {
    const query = (): MediaQueryList | null => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(PHONE_PORTRAIT) : null);
    const [on, setOn] = useState(() => query()?.matches ?? false);
    useEffect(() => {
        const mq = query();
        if (!mq) return;
        const change = (): void => setOn(mq.matches);
        change();
        mq.addEventListener('change', change);
        return () => mq.removeEventListener('change', change);
    }, []);
    return on;
}

/** On a phone, the one pane docked under the bench. One always shows. */
type Sheet = 'parts' | 'results' | 'presets';

/** The icon set predates the engine's kind keys by one rename, and a picture
 *  of a ramp is a ramp whatever the code calls the slab. */
const ICON: Record<string, PartGlyph> = {
    anchor: 'anchor', sheave: 'pulley', weight: 'block',
    table: 'table', incline: 'ramp', springBalance: 'gauge',
};

/**
 * How long the transport runs for.
 *
 * To the last event, plus a moment to see the machine standing at rest — an
 * animation that stops on the instant of the last event looks like it was cut
 * off. A machine with nothing to hit (a hand paying out rope into an empty
 * room) has no events at all, so it gets a plain few seconds.
 */
function span(run: M.Run): number {
    const last = runDuration(run);
    if (last > 0) return last + 0.6;
    const moving = run.phases[0] && Object.values(run.phases[0].acceleration).some(a => a.some(x => Math.abs(x) > 1e-6));
    return moving ? 4 : 0;
}

export interface MechanicsBenchProps {
    onBack?: () => void;
    /** Open on this world instead of the opening preset — a question's scene.
     *  See docs/FeatureDesigns/question-scenes/question-scenes-target.md. */
    initialWorld?: World;
    /** Open on this textbook setup (a preset's key) rather than the opening one
     *  — how a list of setups sends a student to one. Ignored when
     *  `initialWorld` is given, or when no preset has the key. */
    initialPreset?: string;
    /** The name in the bench's header. Absent, the bench names itself. */
    title?: string;
    /** Send the bench to the host's logs for debugging, resolving to the id
     *  the report was logged under. Absent, there is no Report button. */
    onReport?: (report: { bench: 'mechanics'; world: World; report: string }) => Promise<string>;
    /** Saving to the teacher's own setups — docs/FeatureDesigns/lab-setups.
     *  Absent, the bench has no Save and no "My setups". Its `opened` setup is
     *  what the bench opens on, unless `initialWorld` is given. */
    saving?: LabSaving<L.MechanicsSetupPayload>;
}

/** The version of `MechanicsSetupPayload` this bench writes. */
const SETUP_PAYLOAD_VERSION = 1;
/** A saved setup's value in the setups picker, kept apart from the presets' keys. */
const SAVED = 'saved:';

/** JSON with its keys in order, so two worlds that say the same thing compare
 *  equal however their objects were put together. */
const canonical = (v: unknown): string => JSON.stringify(v, (_k, x: unknown) => (
    x && typeof x === 'object' && !Array.isArray(x)
        ? Object.fromEntries(Object.entries(x as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
        : x));

export function MechanicsBench({ onBack, initialWorld, initialPreset, title = 'Mechanics Bench', onReport, saving }: MechanicsBenchProps): React.JSX.Element {
    const startWorld = initialWorld ?? saving?.opened?.payload.world;
    const [opening] = useState(() => PRESETS.find(p => p.key === initialPreset) ?? openingBench());
    /* The bench opens on a machine, not on an empty room: a sheave bolted to
       the ceiling with a weight on each side. It is the arrangement every
       reader already half-knows, it needs no explaining, and it is one drag
       from being a question — make the two masses equal and it stops. */
    const [world, setWorld] = useState<World>(() => startWorld ?? opening.build());
    const [, setPast] = useState<World[]>([]);
    const [, setFuture] = useState<World[]>([]);
    const [selectedId, setSelected] = useState<string | null>(null);
    const [selectedLink, setSelectedLink] = useState<string | null>(null);
    /** Whether Edit config is open for the thing selected. Opened from the pencil
     *  beside its name, and left open as the selection changes. */
    const [editing, setEditing] = useState(false);
    /** Picking two points to join, and the first of them once picked. */
    const [joining, setJoining] = useState(false);
    // ── an upright phone ──
    const phone = usePhonePortrait();
    /* Parts first: this bench is built, and the parts pane is where a part's
       settings open once one is picked. */
    const [sheet, setSheet] = useState<Sheet>('parts');
    /** A closer look at the room; null is the whole room. Phone only. */
    const [zoom, setZoom] = useState<SceneZoom | null>(null);
    /** Bumped to frame the machine: on opening, from Fit, when an arrangement loads. */
    const [fitKey, setFitKey] = useState(1);
    /** The run bar's second row — step, speed — on a phone, where one row is all it has. */
    const railRef = useRef<HTMLDivElement | null>(null);
    const [first, setFirst] = useState<PortRef | null>(null);
    const [presetKey, setPresetKey] = useState<string>(startWorld ? '' : opening.key);
    // The teacher's saved setups, when the host lets this bench save.
    const setups = useLabSetups<L.MechanicsSetupPayload>('mechanics-bench', SETUP_PAYLOAD_VERSION, saving);
    const [saveDialog, setSaveDialog] = useState<'new' | 'copy' | null>(null);
    /** Where the cursor is on the run, in seconds, and whether it is moving. */
    const [t, setT] = useState(0);
    const [playing, setPlaying] = useState(false);
    /** How fast play runs the clock: 1 is real time, 0.1 a tenth of it. The
     *  run itself does not change — only how quickly the cursor crosses it. */
    const [rate, setRate] = useState(1);
    /** What the bench draws ON ITSELF, over and above the machine. */
    const [showForces, setShowForces] = useState(false);
    const [showMotion, setShowMotion] = useState(true);
    /** Whose pins play pulled, so reset can put those same ones back. */
    const [pulled, setPulled] = useState<string[]>([]);

    const svg = useRef<SVGSVGElement | null>(null);
    /* Full screen, as the chemistry lab has it — the browser's own where it
       will, the bench pinned over the page where it won't (iPhone Safari).
       The button only: the automatic request on turning a phone is written
       against the chemistry lab's sideways layout, which this bench has not. */
    const benchRef = useRef<HTMLDivElement | null>(null);
    const fullscreen = useBenchFullscreen(benchRef, { followRotation: false });
    useBenchTouch();

    /* ── the answer ────────────────────────────────────────────────────────
     *
     * ONE answer, for the bench as it stands. A pinned weight is a weight that
     * cannot move, and the arrangement containing it is a real arrangement
     * with a real answer — so there is nothing here about held benches and
     * released ones, and no second run to keep in step with the first.
     *
     * Letting go is not a way of LOOKING at the bench, it is a change to it:
     * play pulls the pins out and the run follows from what is left. */
    const structural = useMemo(() => diagnose(world), [world]);
    const ok = isBuildable(structural);
    const loose = useMemo(() => released(world), [world]);
    const run = useMemo(() => (ok ? buildRun(world) : null), [world, ok]);
    const problems = useMemo(
        () => (run
            ? diagnose(
                world,
                { acceleration: run.phases[0]?.acceleration ?? {}, force: run.phases[0]?.force ?? {}, active: run.phases[0]?.active ?? [], singular: run.singular, passes: 0 },
                run.phases[0]?.freeBodies,
            )
            : structural),
        [world, run, structural],
    );
    const runnable = isBuildable(problems) && !!run && !run.singular;

    const now = useMemo(() => (run ? at(run, t) : null), [run, t]);
    const shown = useMemo(
        () => (run && runnable ? worldAt(world, run, t) : world),
        [world, run, runnable, t],
    );
    const duration = run ? span(run) : 0;

    /**
     * Every force on every body, as arrows on the bench itself.
     *
     * The free-body diagram in the pane is one body at a time, on its own,
     * which is the right picture for working an answer out. This is the other
     * picture: the whole machine at once, with every pull and push where it
     * actually acts. Off by default, because a bench with forty arrows on it
     * is a bench nobody can read — it is something you turn on to answer a
     * question, not something to build against.
     *
     * Anchored where each force ACTS: a tension at the point the rope touches,
     * everything else at the middle of the body it acts on.
     */
    /**
     * Setting up is a STATICS problem, so before anything is let go the bench
     * shows what holds it STILL — not what it would accelerate at.
     *
     * Those are different questions and the second one was being answered.
     * Ask a half-built bench for accelerations and `T = 0` with everything in
     * free fall is a perfectly self-consistent reply, so a rope that ought to
     * be carrying 19.6 N read 0.0 N, a hook read an empty diagram, and a
     * weight sitting in its start position read 9.80 m/s². The moment the pins
     * come out and the clock runs, the dynamics is the right question again.
     */
    /**
     * The bench standing still, solved properly: every part's own free-body
     * diagram, with the positions and the forces found together.
     *
     * `statics` solved the forces where the parts happened to be and let a
     * separate search shuffle the positions afterwards. That cannot work: a
     * pulley hangs off to one side BECAUSE there is tension, and there is
     * tension because it hangs off to one side, so solved in sequence the first
     * step takes the perfect answer of every tension zero and the second has
     * nothing left to move by.
     */
    const eq = useMemo(() => mechanics.equilibrium(world), [world]);
    /**
     * Is the bench STANDING STILL, and therefore a statics question?
     *
     * Not merely "the clock is at zero". A bench with no arrangement that
     * holds it still is a machine whatever the clock says, and its numbers
     * come from the run — which has them, at t = 0 as at any other moment.
     * Read as the clock alone, an Atwood sat at zero showed `T = ?` on every
     * diagram and "nothing to work out yet" beside a claim stating the very
     * tension it was refusing to print, because the STATICS had no answer and
     * nothing thought to ask the dynamics.
     */
    const atRest = t === 0 && !playing && eq.solved;
    /**
     * How to print a tension — and the difference between the two ways of
     * having no number, which the bench was running together.
     *
     * SLACK means the rope is carrying nothing: a real answer about a real
     * rope, and the whole reason the engine models surplus length. NOT DECIDED
     * means the arrangement does not pin the forces down — two hands on one
     * rope, or a bench still mostly pinned — and says nothing about that rope
     * at all. Printing "slack" for the second put the word "slack" beside a
     * rope the same panel called "exactly taut", which is the bench
     * contradicting itself in two lines.
     */
    const tensionText = (T: number | undefined, _id?: string): string => {
        if (T !== undefined) return `${fixed(T, 1)} N`;
        if (!atRest) return 'slack';
        /* Nothing is decided at all when the bench has no answer. A tension is
         * only missing FOR A REASON — this rope is carrying nothing — once
         * there is an arrangement for it to be carrying nothing in. */
        return eq.solved ? 'slack' : 'not decided';
    };
    const shownFbs = atRest ? [] : (now?.freeBodies ?? []);
    const shownForce = atRest ? eq.force : (now?.force ?? {});
    const arrows = useMemo(() => {
        if (!showForces || (!atRest && (!now || !runnable))) return null;
        const out: { at: { x: number; y: number }; vector: { x: number; y: number }; kind: string; label: string; on?: string }[] = [];
        /* AT REST the arrows are the free-body diagrams the solve produced,
         * one per PART — the same objects the right-hand pane draws, so an
         * arrow on the bench and a row in the diagram can never disagree. Once
         * the clock runs they come from the dynamics instead. */
        if (atRest) {
            shown.parts.forEach(p => {
                const d = mechanics.diagramOf(world, eq, p.id);
                if (!d) return;
                d.forces.forEach(f => {
                    if (Math.hypot(f.vector.x, f.vector.y) < 1e-6) return;
                    out.push({ at: p.at, vector: f.vector, kind: 'rope', label: f.label, on: p.id });
                });
            });
            return out.length ? out : null;
        }
        shownFbs.forEach(fb => {
            /* At rest there may be no run at all — a half-built bench has
               nothing to run — so the bodies come from the world itself. */
            const body = (run?.bodies ?? bodiesOf(shown)).find(b => b.id === fb.bodyId);
            const home = body && (body.partIds.map(i => partById(shown, i)).find(p => p && p.kind === 'weight')
                ?? body.partIds.map(i => partById(shown, i)).find(Boolean));
            if (!home) return;
            [...fb.forces, ...(fb.hold ? [{ kind: 'normal', label: 'hold', vector: fb.hold, at: undefined }] : [])]
                .forEach(f => {
                    if (Math.hypot(f.vector.x, f.vector.y) < 1e-6) return;
                    out.push({ at: f.at ?? home.at, vector: f.vector, kind: f.kind, label: f.label, on: home.id });
                });
        });
        return out.length ? out : null;
    }, [showForces, now, runnable, run, shown, world, eq, atRest, shownFbs]);

    /**
     * The whole bench as text, for pasting into a bug report.
     *
     * Positions, joints, and what the STATICS makes of it — the forces on
     * every part with the bench standing still, which is the question a setup
     * is actually asking. Derived, never stored, so it cannot go stale.
     */
    /** "Copy the bench" says it did, for a moment. */
    const [benchCopied, setBenchCopied] = useState(false);
    /** Where a Report got to: sending, the id it was logged under, or that it failed. */
    const [reported, setReported] = useState<string | null>(null);
    const report = useMemo(() => {
        const name = (id: string): string => {
            const p = partById(world, id);
            return p ? defOf(p).name(p as never, world) : id;
        };
        const bodies = mechanics.bodiesOf(mechanics.released(world));
        const lines: string[] = [];
        lines.push(`# mechanics bench — ${world.parts.length} parts, ${world.links.length} joints, g = ${world.gravity}`);
        lines.push('');
        lines.push('## parts');
        world.parts.forEach(p => {
            const d = p.data as { mass?: number; radius?: number; wrap?: number; muTop?: number; muLeft?: number; muRight?: number; floorGrip?: number | 'fixed' };
            const bits = [
                `(${p.at.x.toFixed(3)}, ${p.at.y.toFixed(3)})`,
                d.mass !== undefined ? `${d.mass} kg` : '',
                d.radius !== undefined ? `disc r ${d.radius}${d.wrap !== undefined ? ` groove r ${d.wrap}` : ''}` : '',
                d.muTop || d.muLeft || d.muRight ? `mu top ${d.muTop ?? 0} left ${d.muLeft ?? 0} right ${d.muRight ?? 0}` : '',
                d.floorGrip !== undefined ? `floor grip ${d.floorGrip}` : '',
                p.pinned ? 'PINNED' : 'free',
                p.support ? `on ${name(p.support)}${p.face ? ` (${p.face})` : ''}` : '',
            ].filter(Boolean);
            lines.push(`  ${name(p.id)} [${p.kind}] ${bits.join('  ')}`);
        });
        lines.push('');
        lines.push('## joints');
        world.links.forEach(l => {
            if (l.type === 'bond') {
                lines.push(`  bolt: ${mechanics.portLabel(world, l.hangs)} — ${mechanics.portLabel(world, l.holds)}`);
            } else {
                const spare = mechanics.slackOf(world, l);
                lines.push(`  ${l.rigid ? 'rod' : 'rope'} ${l.id}: ${l.path.map(q => mechanics.portLabel(world, q)).join(' → ')}`
                    + `  length ${l.length.toFixed(3)} m, run ${mechanics.measureRope(world, l).toFixed(3)} m`
                    + (spare > 1e-6 ? `, SLACK ${spare.toFixed(3)} m` : ''));
            }
        });
        lines.push('');
        lines.push('## runs');
        mechanics.segmentsOf(world).forEach(g => {
            const off = Math.abs(g.pa.x - g.pb.x);
            lines.push(`  ${mechanics.portLabel(world, g.a)} → ${mechanics.portLabel(world, g.b)}`
                + `  (${g.pa.x.toFixed(3)},${g.pa.y.toFixed(3)}) → (${g.pb.x.toFixed(3)},${g.pb.y.toFixed(3)})`
                + `  ${g.length.toFixed(3)} m${off < 1e-9 ? ', vertical' : `, off-vertical ${off.toFixed(3)} m`}`);
        });
        lines.push('');
        lines.push(`## statics — ${eq.solved ? `solved in ${eq.steps} steps` : eq.fault?.kind === 'indeterminate' ? 'INDETERMINATE (nothing decides where the parts stand)' : 'NO EQUILIBRIUM'}`);
        eq.unknowns.forEach(v => lines.push(`  ${v.label} = ${eq.solved ? `${fixed(v.value, 4)} ${v.unit}` : '—'}`));
        (eq.fault?.adrift ?? []).forEach(a => lines.push(`  ✗ ${a.name} cannot be balanced where it stands`));
        lines.push('');
        lines.push('## free-body diagram of each part');
        world.parts.forEach(p => {
            const d = mechanics.diagramOf(world, eq, p.id);
            if (!d) return;
            lines.push(`  ${d.name}${d.held ? ' (held)' : ''}  mass ${d.mass} kg  ΣF = (${fixed(d.net.x, 4)}, ${fixed(d.net.y, 4)})`);
            d.forces.forEach(f => lines.push(`      ${f.label}  (${fixed(f.vector.x, 4)}, ${fixed(f.vector.y, 4)}) N`));
        });
        bodies.forEach(() => undefined);
        if (now) {
            lines.push('');
            lines.push('## dynamics at the cursor (what play would do)');
            now.freeBodies.forEach(fb => {
                lines.push(`  ${fb.name}: ΣF = (${fixed(fb.net.x, 3)}, ${fixed(fb.net.y, 3)}) N`);
                fb.forces.forEach(f => lines.push(`      ${f.label}  (${fixed(f.vector.x, 3)}, ${fixed(f.vector.y, 3)}) N`));
            });
        }
        lines.push('');
        lines.push('## problems');
        problems.forEach(p => lines.push(`  [${p.level}] ${p.text}`));
        return lines.join('\n');
    }, [world, now, problems]);

    /**
     * What each moving mass is accelerating at, as a vector on the bench.
     *
     * Only while it is NOT playing, which is every moment a reader is actually
     * reading: paused anywhere along the run is the state the bench is in when
     * somebody is looking at it, and an acceleration is the one number a
     * picture of a machine cannot show by itself. During playback the motion
     * is the motion, and an arrow on top of it is clutter.
     *
     * One entry per BODY, hung on the part a reader thinks of it as, so a load
     * bolted to a sheave gets one arrow rather than two.
     */
    /** Per-axis numbers summed back into bench coordinates. Every part has two
     *  coordinates now, so there is no index that means "the" anything. */
    const inBench = (b: M.Body, q: number[] | undefined): { x: number; y: number; size: number } => {
        const v = b.axes.reduce(
            (s, ax, i) => ({ x: s.x + ax.x * ((q ?? [])[i] ?? 0), y: s.y + ax.y * ((q ?? [])[i] ?? 0) }),
            { x: 0, y: 0 },
        );
        return { ...v, size: Math.hypot(v.x, v.y) };
    };

    /** One arrow per BODY, hung on the part a reader thinks of it as — so a
     *  load bolted to a sheave gets one arrow rather than two. */
    const motionArrows = useCallback((per: Record<string, number[]>) => {
        if (!showForces || !now || !runnable || !run) return null;
        const out: Record<string, { x: number; y: number; size: number }> = {};
        run.bodies.filter(isFree).forEach(b => {
            const v = inBench(b, per[b.id]);
            if (v.size < 1e-6) return;
            const on = b.partIds.find(id => partById(world, id)?.kind === 'weight') ?? b.partIds[0];
            out[on] = v;
        });
        return Object.keys(out).length ? out : null;
    }, [showForces, now, runnable, run, world]);

    const motion = useMemo(() => motionArrows(now?.acceleration ?? {}), [motionArrows, now]);
    /* Velocity too, and BOTH while it is running.
     *
     * An acceleration is the one number a picture of a machine cannot show by
     * itself, which is why it was drawn while paused. Speed it can show — but
     * only as "that one is faster than this one", and a reader asking how fast
     * wants the number. Shown together they also say the thing neither says
     * alone: a weight can be moving up and speeding up downward, and that is
     * the moment a machine turns round. */
    const speed = useMemo(() => motionArrows(now?.v ?? {}), [motionArrows, now]);

    // ── history ───────────────────────────────────────────────────────────
    /* Back to the start, and the pins go back in — the exact ones play pulled,
       which is why they are remembered rather than re-derived. */
    const rewind = useCallback(() => {
        setPlaying(false);
        setT(0);
        setPulled(ids => {
            if (ids.length) setWorld(w => ids.reduce((x, id) => pin(x, id, true), w));
            return [];
        });
    }, []);

    /** Change the bench and remember what it was. Undo is the only history,
     *  and every change is one entry — a drag is a single edit when the finger
     *  lifts, not one per pointermove, because what moves under the finger is
     *  a ghost and the bench itself does not change until then. */
    const edit = useCallback((f: (w: World) => World) => {
        setWorld(prev => { setPast(p => [...p.slice(-40), prev]); return f(prev); });
        setFuture([]);
        rewind();
    }, [rewind]);

    // ── the transport ─────────────────────────────────────────────────────
    /* Real time in, bench time out. The cursor is the only thing that moves;
       every position is then read from the run, so a slow frame loses nothing
       and a fast one invents nothing. */
    useEffect(() => {
        if (!playing || !runnable) return;
        let raf = 0, last = 0;
        const tick = (ms: number): void => {
            if (!last) { last = ms; raf = requestAnimationFrame(tick); return; }
            const dt = Math.min(0.05, (ms - last) / 1000);
            last = ms;
            setT(prev => {
                const next = prev + dt * rate;
                if (next >= duration) { setPlaying(false); return duration; }
                return next;
            });
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playing, runnable, duration, rate]);

    /**
     * Let go.
     *
     * Nothing more than that: every pin comes out, and whatever the bench does
     * next is what the bench does. There is no second mode and no view to
     * switch — pulling the pins is a change to the arrangement, so the one run
     * it already has simply becomes the run of a bench with no pins in it.
     */
    /**
     * Take a pin out — and if it was the LAST one, let the bench go.
     *
     * Pulling the last pin and pressing play are the same gesture said two
     * ways: in both, nothing is being held any more, and what happens next is
     * what the bench does. Making the reader then find the play button is the
     * bench pretending it does not already know.
     *
     * Only the last one. Freeing a weight while another is still held is the
     * reader setting the arrangement up, not starting it.
     */
    const letGo = (id: string): void => {
        edit(w => pin(w, id, false));
        const after = world.parts.filter(p => p.pinned && p.id !== id);
        if (after.length) return;
        setT(0);
        setPlaying(true);
    };

    /** How far one press of ⏭ carries the clock. Long enough to see something
     *  change, short enough that nothing important happens unseen. */
    const STEP_BY = 0.2;

    /**
     * Forward one step, and stop there.
     *
     * Pulls the pins first if any are in, so it starts a run exactly as ▶
     * does — otherwise the first press would only release the bench and the
     * second would be the one that moved it, which reads as a dropped click.
     */
    /** Back one step. Never pulls a pin — going backwards is reading the run
     *  you already have, not starting one. */
    const stepBack = (): void => {
        if (!runnable || duration <= 0) return;
        setPlaying(false);
        setT(x => Math.max(0, x - STEP_BY));
    };

    const step = (): void => {
        if (world.parts.some(p => p.pinned)) {
            setPulled(was => [...was, ...world.parts.filter(p => p.pinned).map(p => p.id)]);
            setWorld(w => released(w));
            setT(0);
            setPlaying(false);
            return;
        }
        if (!runnable || duration <= 0) return;
        setPlaying(false);
        setT(x => Math.min(duration, x + STEP_BY));
    };

    const play = (): void => {
        const pins = world.parts.filter(p => p.pinned).map(p => p.id);
        if (pins.length) {
            setPulled(was => [...was, ...pins]);
            setWorld(w => released(w));
            setT(0);
            setPlaying(true);
            return;
        }
        if (!runnable || duration <= 0) return;
        if (t >= duration) setT(0);
        setPlaying(p => !p);
    };

    const loadPreset = (key: string): void => {
        const p = PRESETS.find(x => x.key === key);
        if (!p) return;
        openWorld(p.build(), key);
        setups.leave();
    };

    /** One of the teacher's own setups, onto the bench as a preset is. */
    const loadSaved = async (id: string): Promise<void> => {
        const payload = await setups.load(id);
        if (payload) openWorld(payload.world, '');
    };
    const pick = (value: string): void => {
        if (value.startsWith(SAVED)) void loadSaved(value.slice(SAVED.length));
        else if (value) loadPreset(value);
    };

    /** A whole arrangement onto the bench — a preset's, or a saved one. */
    const openWorld = (next: World, key: string): void => {
        setPast(h => [...h.slice(-40), world]);
        /* Forget which pins the last run pulled BEFORE the new bench arrives.
         *
         * `rewind` puts them back by id, and ids repeat across benches — `p1`
         * is the first pulley on every one of them. So loading a preset after
         * playing another put pins into parts of the new bench that nobody had
         * ever held, and a preset built to stand on its own opened held in
         * three places. A run belongs to the bench it was run on. */
        setPulled([]);
        setWorld(next);
        setPresetKey(key);
        setZoom(null);
        setFitKey(k => k + 1);
        setSelected(null);
        setSelectedLink(null);
        setJoining(false);
        setFirst(null);
        setFuture([]);
        rewind();
    };

    // ── saving ────────────────────────────────────────────────────────────
    /* What is saved is the arrangement as BUILT: play takes the pins out, and
       a setup saved mid-run still opens held, as it was set up. */
    const builtWorld = useMemo(
        () => (pulled.length ? pulled.reduce((w, id) => pin(w, id, true), world) : world),
        [world, pulled],
    );
    const savedWorld = setups.enabled ? setups.current?.saved.world : undefined;
    /** Whether the bench differs from the saved setup it is showing. By value:
     *  a run and its reset make new objects that say the same thing. */
    const unsaved = useMemo(
        () => (savedWorld ? savedWorld !== builtWorld && canonical(savedWorld) !== canonical(builtWorld) : false),
        [savedWorld, builtWorld],
    );
    /** Save over the open setup; with none open, name a new one first. */
    const saveNow = (): void => {
        if (setups.busy) return;
        if (setups.current) void setups.save({ payload: { world: builtWorld } });
        else setSaveDialog('new');
    };
    const saveFromDialog = async (name: string, notes: string, chapterIds?: string[]): Promise<void> => {
        const ok = await setups.save({
            payload: { world: builtWorld }, name, notes, asNew: true,
            basedOn: presetKey ? { preset: presetKey } : undefined,
            chapterIds,
        });
        if (ok) setSaveDialog(null);
    };
    const shownTitle = setups.current ? setups.current.name : title;
    const saveRef = useRef(saveNow);
    saveRef.current = saveNow;
    useEffect(() => {
        if (!setups.enabled) return;
        const key = (e: KeyboardEvent): void => {
            if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 's') return;
            // The browser's own Save Page is never what a teacher on the bench means.
            e.preventDefault();
            saveRef.current();
        };
        window.addEventListener('keydown', key);
        return () => window.removeEventListener('keydown', key);
    }, [setups.enabled]);
    /** The claim beside the picker: a preset's, or the teacher's own notes. */
    const savedNotes = !presetKey && setups.current?.notes ? setups.current.notes : null;

    // ── placing, removing, editing ────────────────────────────────────────
    const addPart = (kindKey: string): void => {
        let id = '';
        edit(w => { const r = place(w, kindKey); id = r.id; return r.world; });
        // Not selected mid-join: selecting would move the point the gesture is
        // running on out from under it.
        if (!joining && id) setSelected(id);
        setPresetKey('');
    };

    /** The Connector tile: a rope with two loose ends, and nothing else changes —
     *  no solve, no mode, no selection. Its ends are tied on by dragging them. */
    const addRope = (): void => edit(w => placeRope(w).world);

    const removeSelected = (): void => {
        if (!selectedId) return;
        edit(w => removePart(w, selectedId));
        setSelected(null);
    };

    const removeSelectedLink = (): void => {
        if (!selectedLink) return;
        edit(w => removeLink(w, selectedLink));
        setSelectedLink(null);
    };

    /**
     * Delete takes the selection off the bench.
     *
     * Whatever is selected — a part or a join — because the reader's idea of
     * "this one" is whatever is highlighted, and having the key work on one
     * but not the other is worse than it not working at all.
     *
     * Not while TYPING. A mass is set in a number field and a rope is renamed
     * in a text one, and a bench that deleted the pulley because the reader
     * backspaced over a digit would be unusable. Backspace counts as delete
     * because on a Mac it is the only delete most keyboards have.
     */
    useEffect(() => {
        const key = (e: KeyboardEvent): void => {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            const on = e.target as HTMLElement | null;
            const tag = on?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || on?.isContentEditable) return;
            if (!selectedId && !selectedLink) return;
            e.preventDefault();
            if (selectedId) removeSelected();
            else removeSelectedLink();
        };
        window.addEventListener('keydown', key);
        return () => window.removeEventListener('keydown', key);
    });

    // ── dragging ──────────────────────────────────────────────────────────
    const drag = useRef<{ id: string; dx: number; dy: number; targets: M.Snap[] } | null>(null);
    /* State for the drawing, a ref for the handler: `up` is registered once
       and would otherwise close over the ghost as it was when it mounted. */
    /* The two panes can be put away: a bench being watched wants the width,
       and the rail and the details are for building and reading, not for
       watching. Both open to start with. */
    const [showRail, setShowRail] = useState(true);
    const [showProps, setShowProps] = useState(true);
    /* Light or dark, the reader's choice — remembered in this browser, and the
       system's own setting until they make one. Stored access can throw (a
       private window, blocked site data), so every read and write is guarded
       and the bench falls back to the system setting rather than failing. */
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        try {
            const saved = window.localStorage.getItem('mbench-theme');
            if (saved === 'light' || saved === 'dark') return saved;
        } catch { /* no stored choice to read */ }
        return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });
    useEffect(() => {
        try { window.localStorage.setItem('mbench-theme', theme); } catch { /* not remembered, still applied */ }
    }, [theme]);
    const [ghost, setGhost] = useState<{ id: string; at: { x: number; y: number }; angle?: number; targets?: M.Snap[]; snap?: M.Snap; preview?: World } | null>(null);
    const ghostRef = useRef(ghost);
    ghostRef.current = ghost;
    /** A rope being pulled out of a pulley's rope piece, from one of its tails. */
    /** A rope being pulled out of a pulley's rope piece, from one of its tails —
     *  and the same gesture for two more things: a join dragged from one point
     *  to the next (`join`), and one end of a line already there being tied
     *  somewhere else (`rewire`). In each the rope is drawn from `from` to the
     *  hand, and let go on a lit point it is made. */
    const [pulling, setPulling] = useState<{
        from: PortRef; to: { x: number; y: number }; targets: M.Snap[]; snap?: M.Snap;
        join?: boolean; rewire?: { ropeId: string; atEnd: boolean };
    } | null>(null);
    const pullingRef = useRef(pulling);
    pullingRef.current = pulling;
    /** The table or wedge showing resize handles, and a handle being dragged. */
    const [resizing, setResizing] = useState<string | null>(null);
    const [reshaped, setReshaped] = useState<World | null>(null);
    const reshaping = useRef<{ id: string; handle: string; to?: { x: number; y: number } } | null>(null);
    /* The bench as it is NOW, for the drag handlers below: they are bound
       once, and a block on a surface needs the current surfaces to know where
       under the pointer it would stand. */
    const worldRef = useRef(world);
    worldRef.current = world;

    /**
     * Screen pixels to bench metres, asked of the SVG rather than re-derived.
     *
     * Interpolating the viewBox across the element's bounding rect assumes the
     * drawing fills that rect. It does not: the scene is width-constrained and
     * height-capped, so `preserveAspectRatio` letterboxes it inside its own
     * box — and every drag landed on empty air. `getScreenCTM()` is the
     * transform the browser actually used, letterboxing included.
     */
    const toBench = (e: { clientX: number; clientY: number }): { x: number; y: number } => {
        const el = svg.current;
        const m = el?.getScreenCTM();
        if (!el || !m) return { x: 0, y: 0 };
        const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
        // The page's y runs down; the bench's runs up — see the scene's flip.
        const room = worldRef.current.room;
        return { x: p.x, y: room.ceiling + room.floor - p.y };
    };

    /**
     * Let go on a point it snapped to: put the part THERE, then join. A rope end
     * ties on and is used up; a pulley's axle is bolted, the point it landed on
     * first so the pulley is the side that hangs — flush, if that is a surface.
     */
    const dropJoin = (w0: World, id: string, at: { x: number; y: number }, s: M.Snap): World => {
        const w = movePart(w0, id, at);
        return s.kind === 'bond'
            ? fasten(w, s.to, s.from, k => mintId(w, k))
            : joinPorts(w, s.from, s.to, k => mintId(w, k));
    };

    /** Double-click while building: a table or a wedge shows its corners to drag. */
    const onPartDoubleClick = (id: string): void => {
        const part = partById(world, id);
        if (playing || t > 0 || joining || !part || !defOf(part).handles) return;
        setResizing(id);
        setSelected(id);
        setSelectedLink(null);
    };
    const onHandlePointerDown = (id: string, handle: string, e: React.PointerEvent): void => {
        (e.target as Element).setPointerCapture?.(e.pointerId);
        reshaping.current = { id, handle };
    };

    /** A pulley's rope piece, pressed at a tail: pull a rope out of that rim. */
    const onTailPointerDown = (ref: PortRef, e: React.PointerEvent): void => {
        setPulling({ from: ref, to: toBench(e), targets: mechanics.joinTargets(world, ref) });
    };

    /** While joining, a press on a point starts a join dragged from it: the rope
     *  follows the hand, and letting go on a lit point makes the join. A tap is
     *  still a tap — it picks the point, as before, and a second tap joins. */
    const onPortPointerDown = (ref: PortRef, e: React.PointerEvent): void => {
        if (!joining) return;
        setPulling({ from: ref, to: toBench(e), targets: mechanics.joinTargets(world, ref), join: true });
    };

    /** A press on one end of the selected rope or rod: that end follows the hand
     *  from the stop it hangs from, and is tied wherever it is let go on a lit
     *  point. Let go on nothing and it stays where it was. */
    const onRopeEndPointerDown = (ropeId: string, atEnd: boolean, e: React.PointerEvent): void => {
        const rope = world.links.find(l => l.id === ropeId);
        if (!rope || rope.type !== 'rope' || rope.path.length < 2 || playing || t > 0) return;
        const from = atEnd ? rope.path[rope.path.length - 2] : rope.path[1];
        setPulling({ from, to: toBench(e), targets: mechanics.ropeEndTargets(world, ropeId, atEnd), rewire: { ropeId, atEnd } });
    };

    const onPartPointerDown = (id: string, e: React.PointerEvent): void => {
        /* A gesture in progress owns the press. Selecting on pointerdown
           destroyed the very gesture that needed the selection. */
        /* A rope end or a pulley is picked up even mid-join: dragging one onto a
           point is the other way of joining it, and the rope just laid from the
           Connector tile is laid by dragging its ends. */
        const grabbed = partById(world, id);
        const snaps = !!grabbed && !first && mechanics.portsOf(world, grabbed).some(q => q.joins.snaps);
        if (joining && !snaps) {
            /* With one point already picked, a table or a wedge is somewhere to
               join ANYWHERE along its top: the point on it straight above or
               below where it was pressed. Two points that are both structure
               or both loose still make a rope; a pulley fastened to a surface
               is a bolt, so the join is made one. */
            const hit = partById(world, id);
            const from = first;
            if (!from || !hit || !defOf(hit).mountAt) return;
            const x = toBench(e).x;
            const m = mechanics.mountAt(world, id, x);
            if (!m) return;
            /* The surface point FIRST: a bolt records which side hangs and
               which holds, and the one joined second is the one that hangs —
               joined the other way round the wedge "hung" from the pulley, and
               a bolt recorded backwards draws no strap at all. */
            const done = fasten(m.world, m.ref, from, k => mintId(m.world, k));
            const added = done.links.find(l => !m.world.links.some(o => o.id === l.id));
            edit(() => mechanics.dropIdlePins(done));
            setFirst(null);
            setJoining(false);
            setSelected(null);
            setPresetKey('');
            if (added) setSelectedLink(added.id);
            return;
        }
        const part = partById(world, id);
        if (!part) return;
        const p = toBench(e);
        drag.current = { id, dx: part.at.x - p.x, dy: part.at.y - p.y, targets: mechanics.snapTargets(world, id) };
        (e.target as Element).setPointerCapture?.(e.pointerId);
        setSelected(id);
        setSelectedLink(null);
    };

    /**
     * A drag moves a GHOST. Nothing on the bench moves until you let go.
     *
     * Moving the part for real on every pointermove meant the reader watched a
     * bench pass through states it is not in: ropes going slack because the
     * length had nowhere to go yet, wheels sitting where nothing holds them,
     * and forces answering a question about a bench nobody had asked for. The
     * two honest choices were to show nothing until the gesture ends, or to
     * re-solve the whole statics on every move — and the second cannot be kept
     * to, because a part held in mid-air often has no static answer at all, so
     * it would fall back to exactly the picture being complained about.
     *
     * So: an outline at the cursor, and one edit when the finger comes off.
     * `movePart` reconciles the rope lengths, `stands` asks where that leaves
     * everything, and the undo stack gets one entry for the whole gesture.
     */
    useEffect(() => {
        const move = (e: PointerEvent): void => {
            /* A corner being dragged: the shape follows the hand, drawn from a
               preview, and the bench is asked once when it is let go. */
            const rs = reshaping.current;
            if (rs) {
                rs.to = toBench(e);
                setReshaped(mechanics.reshape(worldRef.current, rs.id, rs.handle, rs.to));
                return;
            }
            const pl = pullingRef.current;
            if (pl) {
                const to = toBench(e);
                setPulling({ ...pl, to, snap: mechanics.nearestTarget(pl.targets, to) });
                return;
            }
            const d = drag.current;
            if (!d) return;
            const p = toBench(e);
            const to = { x: p.x + d.dx, y: p.y + d.dy };
            /* A block standing on a surface is dragged by x alone: the ghost
               sits where the surface is under the pointer, turned to lie
               along it — the same answer the drop will give, so what you see
               while dragging is where it will be. */
            const now = worldRef.current;
            const part = partById(now, d.id);
            const seat = part?.support ? mechanics.seatAt(now, d.id, to.x, part.support) : undefined;
            if (part && seat) {
                const f = mechanics.frameOf(now, { ...part, at: seat.at, face: seat.face });
                setGhost({ id: d.id, at: seat.at, angle: Math.atan2(f.y, f.x) });
                return;
            }
            /* Near a point it can join, the ghost jumps so its point sits ON
               that one — where it will be, not where the pointer is. */
            const snap = part ? mechanics.snapNear(now, d.targets, d.id, to) : undefined;
            const from = snap && mechanics.portAt(now, snap.from);
            const at = part && snap && from
                ? { x: part.at.x + snap.at.x - from.at.x, y: part.at.y + snap.at.y - from.at.y }
                : to;
            /* A rope or rod END is not a ghost: the end itself goes where the
               hand is, and its line is re-measured every move — taut, and a rod
               exactly the gap. Drawn from a preview, so the bench's answer is
               not re-solved on every move; it is asked once, on letting go. */
            const preview = part ? pullEndTo(now, d.id, at) : now;
            setGhost({ id: d.id, at, targets: d.targets, snap, preview: preview !== now ? preview : undefined });
        };
        const up = (): void => {
            const rs = reshaping.current;
            if (rs) {
                reshaping.current = null;
                setReshaped(null);
                const to = rs.to;
                if (to) edit(w => mechanics.resizePart(w, rs.id, rs.handle, to));
                return;
            }
            /* A rope pulled from a tail joins where it snapped, and nowhere else:
               let go over nothing and it goes back into the wheel. */
            const pl = pullingRef.current;
            if (pl) {
                setPulling(null);
                const s = pl.snap;
                if (!s) return;
                const rw = pl.rewire;
                if (rw) {
                    edit(w => mechanics.dropIdlePins(mechanics.moveRopeEnd(w, rw.ropeId, rw.atEnd, s.to)));
                } else if (pl.join) {
                    /* The same join a second tap would make — a pulley's axle
                       onto a surface is still a bolt — and joining is done. */
                    edit(w => {
                        const pair = fastening(w, s.from, s.to);
                        return mechanics.dropIdlePins(pair
                            ? fasten(w, pair[0], pair[1], k => mintId(w, k))
                            : joinPorts(w, s.from, s.to, k => mintId(w, k)));
                    });
                    setFirst(null);
                    setJoining(false);
                    setSelected(null);
                } else {
                    edit(w => mechanics.dropIdlePins(joinPorts(w, s.from, s.to, k => mintId(w, k))));
                }
                setPresetKey('');
                return;
            }
            const d = drag.current;
            drag.current = null;
            setGhost(null);
            if (!d || !ghostRef.current) return;
            const to = ghostRef.current.at;
            const snap = ghostRef.current.snap;
            const preview = ghostRef.current.preview;
            if (preview) {
                /* What was on screen is what is kept: the end where it was let
                   go, its line as long as it was drawn — then tied on, if it
                   snapped, or asked where everything stands, once. */
                const next = snap
                    ? joinPorts(preview, snap.from, snap.to, k => mintId(preview, k))
                    : mechanics.stands(preview);
                edit(() => mechanics.dropIdlePins(next));
                if (snap) { setSelected(null); setPresetKey(''); }
                return;
            }
            if (snap) {
                const next = dropJoin(worldRef.current, d.id, to, snap);
                edit(() => mechanics.dropIdlePins(next));
                setSelected(null);
                setPresetKey('');
                return;
            }
            edit(w => {
                const moved = movePart(w, d.id, to);
                /* Put down on a surface, it STAYS put down: pinned, so letting
                   go of a block on a slope does not start it sliding before
                   the reader has finished building. Play pulls the pin. */
                return mechanics.stands(partById(moved, d.id)?.support ? pin(moved, d.id, true) : moved);
            });
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edit]);

    // ── joining ───────────────────────────────────────────────────────────
    const onPort = (ref: PortRef): void => {
        if (!joining) return;
        if (!first) { setFirst(ref); return; }
        if (portKey(first) === portKey(ref)) { setFirst(null); return; }   // clicked it again
        const a = first;
        /* A pulley's axle joined to a table or a wedge is fastened to it — a
           bolt, flush, whichever point was clicked first. The engine's rule:
           `fastening`. Either can still be changed: Joined by, and the gap. */
        const pair = fastening(world, a, ref);
        const next = pair
            ? fasten(world, pair[0], pair[1], k => mintId(world, k))
            : joinPorts(world, a, ref, k => mintId(world, k));
        edit(() => mechanics.dropIdlePins(next));
        setFirst(null);
        /* Done means done. Staying in joining saved a click on a bench that
           needs several — a rope over a sheave is two joins — but it left the
           control reading "Stop joining" the moment after a join had plainly
           succeeded, which reads as "that did not take". */
        setJoining(false);
        setSelected(null);
        setPresetKey('');
    };

    /**
     * The points worth drawing while joining — the engine's answer, not a
     * guess from the drawing. With a point picked it is where THAT join can
     * end; without one it is anywhere a join can start: a point with room, or
     * a rope's loose end or open rim.
     */
    const joinFrom = first;
    const offered = useMemo(() => {
        const out = new Set<string>();
        if (!joining) return out;
        world.parts.flatMap(p => mechanics.portsOf(world, p)).forEach(port => {
            const ref = { partId: port.partId, portId: port.id };
            const ok = (first && portKey(first) === portKey(ref))           // picked: click again to drop it
                || (joinFrom ? canJoin(world, joinFrom, ref) : canTake(world, ref, 'rope') || carriesFrom(world, ref));
            if (ok) out.add(portKey(ref));
        });
        return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [world, joining, first]);

    const selected = selectedId ? partById(world, selectedId) : undefined;
    /* A pinned part opens with its edit section showing, so Unpin is in view the
       moment it is selected. Only on selecting it: the pencil still closes it. */
    useEffect(() => {
        if (selectedId && partById(world, selectedId)?.pinned) setEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);
    /** Would this part move if every pin came out? Only then is a pin doing
     *  anything — but a pin already IN can always be taken out, which is the
     *  point below. */
    const wouldMove = !!selected && !!bodiesOf(loose).find(b => b.partIds.includes(selected.id) && isFree(b));
    const canPin = wouldMove || !!selected?.pinned;
    const link = selectedLink ? world.links.find(l => l.id === selectedLink) : undefined;
    const holders = selectedId ? bondsHolding(world, selectedId) : [];
    /**
     * What the bench is still complaining about, once you are WATCHING it.
     *
     * "There is no arrangement of forces that holds this still" is a true and
     * useful thing to say to somebody looking at a bench that cannot stand —
     * it is the invitation to press play. Said to somebody who HAS pressed
     * play, and is stepping through the run it invited them to, it is
     * answering a question they have left behind, and the red rings go on
     * pointing at parts whose only fault is that they are moving.
     *
     * So `whileStill` reports last exactly as long as standing still is the
     * question. The clock moving is the moment it stops being.
     */
    const watching = playing || t > 0;
    /* How far down the stage the ceiling line is, as a percentage: the view
       always fills the stage and starts ROOM_ABOVE_CEILING above the ceiling,
       so this is a fixed share whatever size the window is. The run controls
       are centred on the band above it; the view switches sit just below it. */
    const ceilingPct = (ROOM_ABOVE_CEILING / (world.room.ceiling - world.room.floor + ROOM_ABOVE_CEILING + ROOM_BELOW_FLOOR)) * 100;
    /* ── the motion of each moving thing, for a tag on the thing itself ───
     *
     * One tag per moving body, on the bench beside it, rather than one table in
     * a corner: while it runs the reader is watching the PARTS, and a number
     * that travels with the part it belongs to is one they never have to match
     * up by name. The same signed figures as Every part — along y as the ruler counts, and
     * rightward for a part going neither up nor down — and only once the clock
     * is running, because at rest every one of them is zero. A body of several
     * bolted parts gets one tag, on its heaviest part. */
    /* Which way y counts on the page. The wall's ruler reads UP from the floor
     * unless something hangs from a ceiling hook, and DOWN from the ceiling when
     * it does (see `readsUp`), and every y a reader sees — the axes, the tags,
     * the tables — counts the way that ruler does. The engine's coordinates
     * run UP; only what is shown turns over, and only when something hangs. */
    const ys = readsUp(world) ? 1 : -1;
    const motionTags = (() => {
        if (!watching || !run || !now) return [];
        return (now.freeBodies ?? []).flatMap(fb => {
            const body = run.bodies.find(b => b.id === fb.bodyId);
            if (!body || !isFree(body)) return [];
            const one = (per: Record<string, number[]> | undefined): number => {
                const u = inBench(body, (per ?? {})[fb.bodyId]);
                const way = Math.abs(u.y) > 1e-9 ? Math.sign(u.y * ys) : Math.sign(u.x) || 1;
                return u.size * way;
            };
            const heaviest = body.partIds
                .map(id => partById(world, id))
                .filter((q): q is NonNullable<typeof q> => !!q)
                .reduce<M.Part | undefined>((best, q) => (!best || defOf(q).mass(q as never) > defOf(best).mass(best as never) ? q : best), undefined);
            // Its title is the part's name, and the mass is the whole body's.
            const mass = body.partIds.reduce((s, id) => {
                const q = partById(world, id);
                return s + (q ? defOf(q).mass(q as never) : 0);
            }, 0);
            return heaviest
                ? [{ partId: heaviest.id, name: fb.name, mass, v: one(now.v), a: one(now.acceleration) }]
                : [];
        });
    })();
    /* ── the forces on each mass, for a tag on the mass itself ─────────────
     *
     * With Forces on, the arrows show which way and how big relative to each
     * other; the tag beside each mass says HOW BIG, in each direction — the
     * same x and y a free-body table is added up in. Read off the arrows the
     * bench is drawing, so a row in the tag and an arrow on the part can never
     * be two answers. Each force by its symbol: mg, T₁, N₂. */
    const forceTags = (() => {
        if (!showForces || !arrows) return [];
        const rowsOf = new Map<string, { label: string; x: number; y: number }[]>();
        arrows.forEach(a => {
            const q = a.on ? partById(world, a.on) : undefined;
            if (!q || defOf(q).mass(q as never) <= 0) return;
            const rows = rowsOf.get(q.id) ?? [];
            rows.push({ label: a.label.split(' ')[0], x: a.vector.x, y: a.vector.y * ys });
            rowsOf.set(q.id, rows);
        });
        return [...rowsOf].map(([partId, rows]) => {
            const q = partById(world, partId) as M.Part;
            return { partId, name: defOf(q).name(q as never, world), mass: defOf(q).mass(q as never), rows };
        });
    })();
    const onShow = watching ? problems.filter(p => !p.whileStill) : problems;
    const stopper = onShow.find(p => p.level === 'stops');
    const bodies = useMemo(() => bodiesOf(world), [world]);
    const byPart = useMemo(() => bodyIndex(bodies), [bodies]);
    const selectedBody = selectedId ? byPart.get(selectedId) : undefined;
    /* The selected PART's own diagram, out of the solved system — its forces
     * are the variables the solve found, so the picture and the arithmetic are
     * the same object. */
    const selectedDiagram = useMemo(
        () => (selectedId ? mechanics.diagramOf(world, eq, selectedId) : null),
        [world, eq, selectedId],
    );
    const selectedFb = shownFbs.find(f => f.bodyId === selectedBody?.id);
    /* After the run has STOPPED — a block on the floor, a load up against its
     * wheel — the run has no diagram left to give: its last phase is the bench
     * standing still, with no forces solved for it. So the forces are worked
     * out again, for where everything ended up. If that arrangement stands
     * (the Atwood's light block against its wheel, the heavy one on the rope)
     * it is its statics; if it still could not hold still, the forces are the
     * ones of the last instant it moved, which are known. */
    const runEnd = run && runnable ? runDuration(run) : 0;
    const ended = runEnd > 0 && t >= runEnd;
    const endEq = useMemo(() => (ended ? mechanics.equilibrium(shown) : null), [ended, shown]);
    const endDiagram = useMemo(
        () => (ended && endEq?.solved && selectedId ? mechanics.diagramOf(shown, endEq, selectedId) : null),
        [ended, endEq, shown, selectedId],
    );
    const lastFb = useMemo(() => {
        if (!ended || endEq?.solved || !run || !selectedBody) return undefined;
        const moving = [...run.phases].reverse().find(ph => ph.freeBodies.length > 0);
        return moving?.freeBodies.find(f => f.bodyId === selectedBody.id);
    }, [ended, endEq, run, selectedBody]);
    /* A rope gets one too. It is not a body — massless, ideal, no ΣF = ma to
     * solve — but it is the diagram that shows WHY both ends pull the same:
     * with no mass its forces must cancel, and over a frictionless wheel that
     * is only possible if they are equal. */
    /* A rope's stops, in order — the nearest thing it has to a name. */
    const pathOf = (l: { path: { partId: string }[] }): string => l.path.map(st => {
        const q = partById(world, st.partId);
        return q ? defOf(q).name(q as never, world) : '?';
    }).join(' \u2192 ');
    const selectedRopeFbs = useMemo(() => {
        const l = selectedLink ? world.links.find(x => x.id === selectedLink) : undefined;
        return l && l.type === 'rope' ? mechanics.ropeDiagrams(world, l, shownForce) : [];
    }, [world, selectedLink, shownForce]);

    /**
     * Every CONNECTOR, not every rope: a bolt is a join between two points too,
     * and it was the one join a reader could not find in any list. It has no
     * tension to show — a bolt makes two parts one body and carries no force of
     * its own — so it says so, and its length is the gap it holds. Unsolved,
     * the connectors are still listed; only their tensions are not known.
     */
    const connectorsTable = (solved: boolean): React.JSX.Element => (
        <div className="mbench-tablewrap">
            <table className="mbench-table">
                <thead><tr><th>Connector</th><th>Tension</th><th>Length</th></tr></thead>
                <tbody>
                    {ropesOf(world).map((r, i) => {
                        const T = shownForce[spanId(r.id, 0)];
                        return (
                            <tr
                                key={r.id}
                                className={r.id === selectedLink ? 'is-sel' : undefined}
                                onClick={() => { setSelectedLink(r.id); setSelected(null); }}
                            >
                                <td>{r.label ?? (ropesOf(world).length > 1 ? `rope ${i + 1}` : 'the rope')}</td>
                                <td>{solved ? tensionText(T, spanId(r.id, 0)) : '—'}</td>
                                {/* The surplus belongs with the length it is part
                                    of, not in a column of its own that is empty on
                                    every rope nobody has loosened. */}
                                <td>{r.length.toFixed(2)} m{slackOf(world, r) ? <em> +{slackOf(world, r).toFixed(2)}</em> : null}</td>
                            </tr>
                        );
                    })}
                    {mechanics.bondsOf(world).map((b, i, all) => (
                        <tr
                            key={b.id}
                            className={b.id === selectedLink ? 'is-sel' : undefined}
                            onClick={() => { setSelectedLink(b.id); setSelected(null); }}
                        >
                            <td>{all.length > 1 ? `bolt ${i + 1}` : 'the bolt'}</td>
                            <td>—</td>
                            <td>{mechanics.boltGap(world, b.id).toFixed(2)} m</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // A phone: a part picked — on the bench, from a chip, or just added — brings
    // its tile and the settings under it into view in Parts.
    useEffect(() => {
        if (!phone || sheet !== 'parts' || (!selectedId && !selectedLink)) return;
        const frame = requestAnimationFrame(() => {
            const rail = railRef.current;
            // The settings box itself: it sits under all the tiles, so bringing
            // the lit tile to the top could still leave the settings off screen.
            const box = rail?.querySelector<HTMLElement>('.mbench-partprops')
                ?? rail?.querySelector<HTMLElement>('.mbench-tile[data-active]');
            if (!rail || !box) return;
            const top = box.getBoundingClientRect().top - rail.getBoundingClientRect().top + rail.scrollTop - 8;
            rail.scrollTo({ top, behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(frame);
    }, [phone, sheet, selectedId, selectedLink]);

    /** What joining asks for, and the key to the colours. Over the bench on a
     *  wide screen; at the top of Parts on a phone, where the bench has no height to spare. */
    const verb = phone ? 'Tap' : 'Click';
    const joinHint = joining ? (
        <div className="mbench-hint">
            <span>
                {first
                    ? `Now ${verb.toLowerCase()} the point it joins to.`
                    : `${verb} a joining point, then the one it joins to — or drag from one to the other. A pulley’s rim is where a rope runs over it; its axle is where it is bolted or tied.`}
            </span>
            {/* A key, because three colours are three colours until
                somebody says what they mean. */}
            <span className="mbench-key" aria-hidden="true">
                <i data-need="needed" /> must join
                <i data-need="optional" /> may
                <i data-need="joined" /> joined
            </span>
        </div>
    ) : null;

    /** The selected part: its name, Edit config and its free-body diagram.
     *  In the runtime pane on a wide screen; under its tile in Parts on a phone. */
    const partBox = selected ? (
                <section className="mbench-selbox">
                    <h3 className="mbench-propshead">
                        {defOf(selected).name(selected as never, world)}
                        <em>
                            {defOf(selected).label}
                            {(defOf(selected).keyFacts?.(selected as never, world) ?? []).map(f => (
                                <span key={f} className="mbench-keyfacts"> · {f}</span>
                            ))}
                        </em>
                        <button
                            type="button"
                            className="mbench-edit"
                            aria-pressed={editing}
                            aria-label={editing ? 'Stop editing' : 'Edit'}
                            title={editing ? 'Stop editing' : 'Edit'}
                            onClick={() => setEditing(v => !v)}
                        >
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 20h4L19 9l-4-4L4 16v4z" />
                                <path d="M13.5 6.5l4 4" />
                            </svg>
                        </button>
                    </h3>

                    {/* Everything that CHANGES the bench, gathered and
                        named. What follows it is the bench answering —
                        forces, diagrams, numbers — and a reader scrolling
                        this rail should be able to see at a glance which
                        half they are in. The controls were loose in the
                        column and read as more readouts.
                        A RailGroup, like every other named section of this
                        rail: one collapse control, one heading style. */}
                    {editing && (
                    <div className="mbench-editbody">

                    {/* NO RADIUS CONTROL — deliberately. A pulley turns
                        the rope and takes none of it: the arc is not
                        counted as rope and the radius appears in no
                        equation, not even for a wheel with inertia, where
                        it cancels. See §5.2 of mechanics-engine-target.md.
                        It is a drawing size and a clearance, so there is
                        nothing here for a reader to learn by changing. It
                        comes back with advanced mechanics. */}

                    {(selected.kind === 'weight' || selected.kind === 'sheave') && (
                        <Slider
                            label="Mass" unit="kg" min={0} max={20} step={0.5}
                            value={(selected.data as { mass?: number }).mass ?? 0}
                            note={`${(world.gravity * ((selected.data as { mass?: number }).mass ?? 0)).toFixed(1)} N`}
                            onChange={v => edit(w => setData(w, selected.id, { mass: v }))}
                        />
                    )}
                    {/* How it grips the floor — its own, not the floor's:
                        what a block is made of decides how it slides. Only
                        while it STANDS on the floor: offered for a block
                        hanging in the air it read as a setting of that block
                        now, and did nothing until it landed. Asked of the
                        bench as shown, so it appears once a run lands it. */}
                    {selected.kind === 'weight' && !selected.support && standsOnFloor(shown, selected.id) && (
                        <FloorGrip
                            legend="On the floor"
                            value={(selected.data as { floorGrip?: number | 'fixed' }).floorGrip ?? 0}
                            onChange={g => edit(w => setData(w, selected.id, { floorGrip: g }))}
                        />
                    )}

                    {/* Fixed or free along the beam. A difference in the
                        PHYSICS, not in the drag: a fixed hook swallows
                        whatever sideways pull a slanted rope gives it, and
                        a runner cannot, so it moves until the rope hangs
                        straight down. */}
                    {/* A PIN, which is not the same as being fixed.
                      *
                      * Only something that could otherwise move can be
                      * pinned — and that is a fact about this part in this
                      * arrangement, not about its kind. A pulley bolted to
                      * the ceiling is already fixed and has nothing to pin;
                      * the same pulley hanging on a rope has. A table never
                      * has. Asking whether it would be free once released
                      * answers all three without a list. */}
                    {canPin && (
                        <>
                            {selected.pinned && (
                                <p className="mbench-claim">
                                    {wouldMove
                                        ? 'Held for now, so the bench can tell you what everything is carrying while nothing moves. Play pulls every pin out at once; reset puts them back.'
                                        /* A pin left over from placing it, on something the bench now
                                           holds by itself. Harmless, and worth saying so — otherwise a
                                           reader wonders which of the two is doing the work. */
                                        : 'This is already held without it, so the pin is doing nothing. Safe to take out.'}
                                </p>
                            )}
                        </>
                    )}

                    {/* What you can DO to it, under how it is held: pin or
                        unpin it, take off a bolt, or delete it. */}
                    <div className="mbench-dorow">
                        {canPin && (
                            <button
                                type="button"
                                className="mbench-do"
                                onClick={() => (selected.pinned ? letGo(selected.id) : edit(w => pin(w, selected.id, true)))}
                            >{selected.pinned ? 'Unpin' : 'Pin'}</button>
                        )}
                        {holders.map(id => {
                            const b = world.links.find(l => l.id === id);
                            const holds = b && b.type === 'bond' ? partById(world, b.holds.partId) : undefined;
                            return (
                                <button key={id} type="button" className="mbench-do" onClick={() => edit(w => removeLink(w, id))}>
                                    Unbolt from {holds ? defOf(holds).name(holds as never, world) : 'it'}
                                </button>
                            );
                        })}
                        <button type="button" className="mbench-do is-danger" onClick={removeSelected}>Delete</button>
                    </div>

                    {/* A plain wheel, or a block: the same part, and the
                        difference is whether it has a shell to tie a dead
                        end to. It is what makes an odd-ratio tackle
                        buildable by hand — with only an axle to tie to,
                        the dead end leaves on the block's centre line and
                        the machine quietly stops being the ratio it looks
                        like. See `SheaveData.becket`. */}
                    {selected.kind === 'sheave' && (
                        <Choice
                            legend="The wheel"
                            value={(selected.data as { becket?: number }).becket === undefined ? 'wheel' : 'block'}
                            options={[{ id: 'wheel', label: 'Plain wheel' }, { id: 'block', label: 'Block with a becket' }]}
                            onPick={v => edit(w => setData(w, selected.id, {
                                becket: v === 'wheel' ? undefined : -3 * ((selected.data as { radius?: number }).radius ?? 0.2),
                            }))}
                        />
                    )}

                    {selected.kind === 'anchor' && (
                        <>
                            <Choice
                                legend="On the beam"
                                value={(selected.data as { slides?: boolean }).slides ? 'runs' : 'bolted'}
                                options={[{ id: 'bolted', label: 'Bolted fast' }, { id: 'runs', label: 'Runs on the beam' }]}
                                onPick={v => edit(w => setData(w, selected.id, { slides: v === 'runs' }))}
                            />
                            {(selected.data as { slides?: boolean }).slides && (
                                <Slider
                                    label="Mass" unit="kg" min={0.1} max={10} step={0.1}
                                    value={(selected.data as { mass?: number }).mass ?? 0.5}
                                    note="a runner has to weigh something — a massless one free to move has no answer"
                                    onChange={v => edit(w => setData(w, selected.id, { mass: v }))}
                                />
                            )}
                        </>
                    )}

                    {selected.kind === 'ropeEnd' && (
                        <>
                            {/* FIXED, not pinned: a hand that holds still is
                                part of what this machine is, and play does
                                not let go of it. */}
                            <Choice
                                legend="This end"
                                value={(selected.data as { pull?: number }).pull === undefined ? 'holds' : 'pulls'}
                                options={[{ id: 'holds', label: 'Holds still' }, { id: 'pulls', label: 'Pulls with…' }]}
                                onPick={v => edit(w => setData(w, selected.id, {
                                    pull: v === 'holds' ? undefined : (selected.data as { pull?: number }).pull ?? 20,
                                }))}
                            />
                            {(selected.data as { pull?: number }).pull !== undefined && (
                                <Slider
                                    label="Force" unit="N" min={0} max={400} step={5}
                                    value={(selected.data as { pull?: number }).pull as number}
                                    onChange={v => edit(w => setData(w, selected.id, { pull: v }))}
                                />
                            )}
                        </>
                    )}

                    {/* Anything with a FACE — asked of the kind, not of its
                        name, so a curved chute added later gets these for
                        free. A table tilted is a ramp; there is no reason to
                        hide the angle on one and not the other. */}
                    {/* A base on the floor, a side rising from each end of
                        it, and a flat top. Upright sides make a table;
                        slope them and the top narrows until the two meet,
                        and it is a wedge. One shape, four numbers. */}
                    {defOf(selected).supports && (() => {
                        const d = selected.data as { base?: number; left?: number; right?: number; height?: number; floorGrip?: number | 'fixed' };
                        const grip = d.floorGrip ?? 'fixed';
                        const base = d.base ?? 2.4;
                        const left = d.left ?? Math.PI / 2;
                        const right = d.right ?? Math.PI / 2;
                        const cap = mechanics.apexHeight(base, left, right);
                        const deg = (r: number): number => Math.round((r * 180) / Math.PI);
                        return (
                            <>
                                <Slider
                                    label="Base" unit="m" min={0.6} max={5} step={0.1} value={base}
                                    onChange={v => edit(w => setData(w, selected.id, {
                                        base: v,
                                        ...(defOf(selected).derived?.includes('height')
                                            ? { height: mechanics.apexHeight(v, left, right) }
                                            : {}),
                                    }))}
                                />
                                {/* Only where it is a number to choose. On a
                                    wedge the height is where the two sides
                                    meet — see `PartKind.derived`. */}
                                {!defOf(selected).derived?.includes('height') ? (
                                    <Slider
                                        label="Height" unit="m" min={0.2} max={+(cap - 0.05).toFixed(2)} step={0.05}
                                        value={Math.min(d.height ?? 1.2, cap - 0.05)}
                                        onChange={v => edit(w => setData(w, selected.id, { height: v }))}
                                    />
                                ) : (
                                    <p className="mbench-claim">
                                        {cap.toFixed(2)} m tall, where its two sides meet — set by the base
                                        and the angles, not on its own.
                                    </p>
                                )}
                                <Slider
                                    label="Left angle" unit="°" min={10} max={90} step={1} value={deg(left)}
                                    onChange={v => edit(w => setData(w, selected.id, {
                                        left: (v * Math.PI) / 180,
                                        // A wedge is as tall as its sides let it be, always.
                                        ...(defOf(selected).derived?.includes('height')
                                            ? { height: mechanics.apexHeight(base, (v * Math.PI) / 180, right) }
                                            : {}),
                                    }))}
                                />
                                <Slider
                                    label="Right angle" unit="°" min={10} max={90} step={1} value={deg(right)}
                                    onChange={v => edit(w => setData(w, selected.id, {
                                        right: (v * Math.PI) / 180,
                                        ...(defOf(selected).derived?.includes('height')
                                            ? { height: mechanics.apexHeight(base, left, (v * Math.PI) / 180) }
                                            : {}),
                                    }))}
                                />
                                {/* Each face its own roughness: a wedge's two
                                    slopes are two questions, and its base on
                                    the floor is a third. */}
                                {(defOf(selected).faces?.(selected as never, world) ?? []).map(f => {
                                    const key = `mu${f.id.charAt(0).toUpperCase()}${f.id.slice(1)}`;
                                    const mu = (selected.data as Record<string, number | undefined>)[key] ?? 0;
                                    const said = f.label.replace(/^the /, '');
                                    return (
                                        <Slider
                                            key={f.id}
                                            label={`${said.charAt(0).toUpperCase()}${said.slice(1)} friction`} unit="μ" min={0} max={1} step={0.05} value={mu}
                                            note={mu ? undefined : 'smooth'} noteInline
                                            onChange={v => edit(w => setData(w, selected.id, { [key]: v }))}
                                        />
                                    );
                                })}
                                {/* Its base: fixed unless the reader lets it
                                    slip. Once it can, it is a body and has a
                                    mass — its size at a fixed density until the
                                    reader sets one. */}
                                <FloorGrip
                                    legend="Base on the floor"
                                    value={grip}
                                    onChange={g => edit(w => setData(w, selected.id, { floorGrip: g }))}
                                />
                                {grip !== 'fixed' && (
                                    <Slider
                                        label="Mass" unit="kg" min={0.5} max={50} step={0.5}
                                        value={+defOf(selected).mass(selected as never).toFixed(1)}
                                        note={(selected.data as { mass?: number }).mass === undefined ? 'from its size' : undefined}
                                        noteInline
                                        onChange={v => edit(w => setData(w, selected.id, { mass: v }))}
                                    />
                                )}
                            </>
                        );
                    })()}

                    {/* Standing on something is an EDIT and not a join, so
                        it is a control here rather than a rope you draw. */}
                    {surfacesFor(world, selected.id).length > 0 && defOf(selected).mass(selected as never) > 0 && !defOf(selected).supports && (
                        <label className="mbench-field">
                            <span>Stands on</span>
                            <select
                                value={selected.support ? `${selected.support}/${selected.face ?? ''}` : ''}
                                onChange={e => edit(w => {
                                    const [on, which] = e.target.value.split('/');
                                    return rest(w, selected.id, on || null, which || undefined);
                                })}
                            >
                                <option value="">— nothing, it hangs —</option>
                                {/* One entry per FACE: a wedge has two slopes
                                    and which one a block is on is the
                                    difference between two questions. */}
                                {surfacesFor(world, selected.id).map(f => (
                                    <option key={`${f.part.id}/${f.face ?? ''}`} value={`${f.part.id}/${f.face ?? ''}`}>{f.label}</option>
                                ))}
                            </select>
                        </label>
                    )}

                    {selected.kind === 'springBalance' && (
                        <p className="mbench-reads">
                            <strong>
                                {reading(world, { acceleration: {}, force: shownForce, active: [], singular: false, passes: 0 }, selected.id) !== null
                                    ? (reading(world, { acceleration: {}, force: shownForce, active: [], singular: false, passes: 0 }, selected.id) as number).toFixed(1)
                                    : '—'}
                            </strong>
                            <span>newtons, the tension of the line it is in</span>
                        </p>
                    )}

                    </div>
                    )}

                    {/* The free-body diagram belongs to the SELECTION, so it
                        lives beside the selection rather than under a bench
                        it says nothing about. */}
                    {/* The statics diagram while the bench stands still, the
                        run's while it is a machine. Both are free-body
                        diagrams of the same part; what differs is whether
                        ΣF has to come to nothing or to `ma`. */}
                    {((atRest && selectedDiagram) || selectedFb || endDiagram || lastFb) && (
                        <RailGroup title="Free-body diagram">
                            {atRest && selectedDiagram
                                ? <PartFBD ys={ys} d={selectedDiagram} />
                                : endDiagram
                                    ? <>
                                        <p className="mbench-fbd-when">Where it stopped, standing still.</p>
                                        <PartFBD ys={ys} d={endDiagram} />
                                    </>
                                    : lastFb
                                        ? <>
                                            <p className="mbench-fbd-when">The last instant before it stopped — it could not stand still where it ended.</p>
                                            <FreeBody ys={ys} fb={lastFb} />
                                        </>
                                        : selectedFb && <FreeBody ys={ys} fb={selectedFb} />}
                        </RailGroup>
                    )}
                </section>
    ) : null;

    /** The selected rope or rod, the same way: beside the Connector tile on a phone. */
    const linkBox = link ? (
                <section className="mbench-selbox">
                    {/* The PATH is the name. A rope is hardly ever given
                        one, so "the rope / ROPE" was a heading that said
                        the same word twice and left the reader to find out
                        further down which rope was selected. */}
                    <h3 className="mbench-propshead">
                        {link.type === 'rope' ? (link.label ?? (link.rigid ? 'the rod' : 'the rope')) : 'the bolt'}
                        <em>
                            {link.type === 'rope' ? `${link.rigid ? 'Rigid rod' : 'Rope'} · ${pathOf(link)}` : 'Bolted join'}
                            <span className="mbench-keyfacts">
                                {' · '}{link.type === 'rope' ? `${link.length.toFixed(2)} m` : `gap ${mechanics.boltGap(world, link.id).toFixed(2)} m`}
                            </span>
                        </em>
                        <button
                            type="button"
                            className="mbench-edit"
                            aria-pressed={editing}
                            aria-label={editing ? 'Stop editing' : 'Edit'}
                            title={editing ? 'Stop editing' : 'Edit'}
                            onClick={() => setEditing(v => !v)}
                        >
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 20h4L19 9l-4-4L4 16v4z" />
                                <path d="M13.5 6.5l4 4" />
                            </svg>
                        </button>
                    </h3>
                    {link.type === 'rope' ? (
                        <>
                            {link.rigid && (
                                <p className="mbench-claim">
                                    {link.length.toFixed(2)} m, and it holds that exactly — a rod pushes
                                    as well as pulls, so it never goes slack and it can stand one part
                                    up on another.
                                </p>
                            )}
                        </>
                    ) : (
                        <p className="mbench-claim">
                            {[link.hangs, link.holds].map(s2 => {
                                const q = partById(world, s2.partId);
                                return q ? defOf(q).name(q as never, world) : '?';
                            }).join(' hangs from ')}
                            <br />Bolted: a bracket. It holds them at a fixed gap AND a fixed
                            {' '}bearing, so it carries a force with two components — sideways as
                            {' '}well as along — and both are in each part&rsquo;s diagram. A rod holds
                            {' '}the distance only, so it can pivot and carries one force along its
                            {' '}own line; a rope holds a maximum distance only, so it pulls and
                            {' '}never pushes.
                        </p>
                    )}

                    {/* Everything that CHANGES this join, in the block
                        that says so — the same division as a part's: what
                        you can alter, then what the bench makes of it. */}
                    {editing && (
                    <div className="mbench-editbody">
                        {/* How much rope there IS — the number a reader
                            would measure with a tape, not a surplus they
                            have to add to something.
                          *
                          * It is still STORED as a surplus, because a
                          * total would be overwritten the next time
                          * anything was dragged: dragging re-measures the
                          * run, and a rope that silently changed length
                          * whenever a part moved would not be a rope. The
                          * slider starts at the taut length, which is the
                          * shortest a rope between these parts can be. */}
                        {link.type === 'rope' && !link.rigid && (() => {
                            /* A winch, both ways. Wound IN it hauls
                               whatever it has most purchase on, as far
                               as the first thing in the way; paid OUT it
                               lets that same thing drop through the
                               slack. The longest worth offering is the
                               height of the room past taut — rope beyond
                               the drop available can never be used,
                               because the floor arrives first. */
                            /* Winding in needs something to haul. A rope
                               strung between two hooks has nothing —
                               shortening it would have to move the beam
                               — so the handle only goes one way, and the
                               bench says why rather than refusing in
                               silence. */
                            const hauls = windable(world, link.id);
                            const spare = slackOf(world, link);
                            const taut = link.length - spare;
                            const least = hauls ? shortestRope(world, link.id) : taut;
                            const most = taut + (world.room.ceiling - world.room.floor);
                            return (
                                <>
                                    <Slider
                                        label="Rope length" unit="m"
                                        min={+least.toFixed(2)} max={+most.toFixed(2)} step={0.02}
                                        value={link.length}
                                        note={spare
                                            ? `${spare.toFixed(2)} m more than the run needs`
                                            : hauls
                                                ? 'exactly taut — wind it in and it hauls'
                                                : 'exactly taut — as long as the run it makes'}
                                        onChange={v => edit(w => setRopeLength(w, link.id, v))}
                                    />
                                    {!hauls && (
                                        <p className="mbench-claim">
                                            Both ends of this are fixed, so there is nothing for it to pull
                                            in — it can only be let out. Move one of the parts to shorten it.
                                        </p>
                                    )}
                                </>
                            );
                        })()}
                    {/* Three kinds of join between the same two points, and
                        the difference is the whole of what is being chosen:
                        a rope may pull and go slack, a rod pulls AND pushes
                        and its force is a number, a bolt is not a force at
                        all. A line threaded over a sheave has more than two
                        ends and is none of the other two. */}
                    {/* A bolt keeps its two points wherever they were
                        when it was made. Closing it to zero is how a
                        pulley fastened ABOVE a surface is brought down
                        onto it. */}
                    {link.type === 'bond' && (() => {
                        const gap = mechanics.boltGap(world, link.id);
                        return (
                            <Slider
                                label="Gap" unit="m" min={0} max={Math.max(1, +gap.toFixed(2))} step={0.01}
                                value={+gap.toFixed(2)}
                                note={gap < 0.005 ? 'flush — the two points meet' : 'between the two bolted points'}
                                onChange={v => edit(w => mechanics.setBoltGap(w, link.id, v))}
                            />
                        );
                    })()}
                    {(link.type === 'bond' || link.path.length === 2) && (
                        <Choice
                            legend="Joined by"
                            value={link.type === 'bond' ? 'bond' : link.rigid ? 'rod' : 'rope'}
                            options={([{ id: 'rope', label: 'Rope' }, { id: 'rod', label: 'Rod' }, { id: 'bond', label: 'Bolt' }] as const)
                                .filter(o => (link.type === 'bond' ? [link.holds, link.hangs] : link.path)
                                    .every(r => canTake(world, r, o.id, link.id)))}
                            onPick={to => edit(w => {
                                const next = retie(w, link.id, to as Parameters<typeof retie>[2]);
                                return mechanics.dropIdlePins(to === 'bond' ? mechanics.seatBolt(next, link.id) : next);
                            })}
                        />
                    )}
                    <button type="button" className="mbench-do is-danger" onClick={removeSelectedLink}>{link.type === 'bond' ? 'Delete bolt' : link.rigid ? 'Delete rod' : 'Delete rope'}</button>
                    </div>
                    )}

                    {/* The rope's own diagrams, for the same reason a part
                        gets one: a reader asked to believe both ends pull
                        equally is owed the arithmetic that makes it so.
                        ONE PER RUN — the runs table above lists the same
                        pieces in the same order, so the table is the index
                        and each diagram is its row worked out. */}
                    {link.type === 'rope' && (
                        <RailGroup title="Free-body diagram">
                            {/* Run by run. A rope is stored as the stops it
                                is threaded through and nothing else, so
                                where it actually lies — and how much of it
                                is on each wheel — is worked out fresh every
                                step. This is that working, shown. */}
                            <div className="mbench-tablewrap">
                                <table className="mbench-table mbench-runs">
                                    <thead><tr><th>Run</th><th>Length</th><th>Tension</th></tr></thead>
                                    <tbody>
                                        {segmentsOf(world).filter(g => g.ropeId === link.id).map(g => {
                                            const from = partById(world, g.a.partId);
                                            const to = partById(world, g.b.partId);
                                            return (
                                                <tr key={g.index}>
                                                    <td>
                                                        {from ? defOf(from).name(from as never, world) : '?'}
                                                        {' → '}
                                                        {to ? defOf(to).name(to as never, world) : '?'}
                                                    </td>
                                                    {/* The straight run IS the run. No "wheel"
                                                        column: a pulley takes no rope, so an arc
                                                        shown in metres beside this one reads as
                                                        something that adds up, and it does not. */}
                                                    <td>{g.length.toFixed(2)} m</td>
                                                    <td>{atRest || (now && runnable) ? tensionText(tensionOf(shownForce, g), spanId(g.ropeId, g.span)) : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {selectedRopeFbs.map(fb => <FreeBody ys={ys} key={fb.bodyId} fb={fb} />)}
                        </RailGroup>
                    )}
                </section>
    ) : null;

    return (
        <div className="mbench bench-palette" ref={benchRef} data-theme={theme} data-sheet={sheet} data-rail={showRail ? 'open' : 'hidden'} data-props={showProps ? 'open' : 'hidden'} data-full={fullscreen.pinned ? 'true' : undefined}>
            <div className="mbench-rail" ref={railRef}>
                <CloseButton className="mbench-paneclose" onClick={() => setShowRail(false)} label="Hide the setup pane" />
                {onBack && <button type="button" className="mbench-back mbench-wide-only" onClick={onBack}>← Back</button>}
                {phone && joinHint}

                {/* At the top of the rail, where the controls are.
                  *
                  * A note laid over the drawing covers the very thing it is
                  * about — a stopping fault is nearly always about a part, and
                  * the strip sat across the beam the part was missing from.
                  * Here it is beside the buttons that fix it, and it does not
                  * move the bench when it appears. */}
                {/* `onShow`, NOT `problems` — the same list the drawing rings.
                    The whileStill rule was written for the red rings and this
                    box went on reading the raw list, so a reader stepping
                    through the run it had invited them into was still being
                    told the bench cannot stand still. */}
                {(onShow.length > 0 || (!!run && !run.exact)) && (
                    <ul className="mbench-alert">
                        {onShow.map((p, i) => (
                            <li key={i} data-level={p.level}>
                                <b aria-hidden="true">{p.level === 'stops' ? '!' : '?'}</b>
                                <span>{p.text}</span>
                            </li>
                        ))}
                        {/* The engine says whether the run is exact. It has
                            always said so and nothing ever passed it on, which
                            meant a bench that had quietly started approximating
                            looked exactly like one that had not. */}
                        {/* Only while there is a run to be inexact ABOUT.
                            It says "the times below are close rather than
                            exact", and at rest there are no times below — the
                            bench is standing still and its answer is a statics
                            answer, which this has nothing to say about. */}
                        {run && !run.exact && !atRest && (
                            <li data-level="warns">
                                <b aria-hidden="true">?</b>
                                <span>
                                    Something here turns as it moves, so the directions change while
                                    it runs and the times below are close rather than exact.
                                    Straightening the runs is what makes the answer closed-form.
                                </span>
                            </li>
                        )}
                    </ul>
                )}

                {/* Tiles, as on the chemistry bench: a drawn part is quicker to
                    recognise than its name, and four of them read as a set of
                    things to pick from rather than a list of commands. */}
                <RailGroup title="Parts" fixed={phone}>
                    <div className="mbench-tiles">
                        {RAIL.map(key => {
                            const kind = allKinds().find(k => k.key === key);
                            if (!kind) return null;
                            const lit = phone && selected?.kind === key;
                            return (
                                <button key={key} type="button" className="mbench-tile" data-active={lit ? 'true' : undefined} title={kind.label} onClick={() => addPart(key)}>
                                    <PartIcon kind={ICON[key]} />
                                    <span>{kind.label}</span>
                                </button>
                            );
                        })}
                        {/* A connector among the parts: a rope with two loose
                            ends, and nothing else on the bench changes. Its ends
                            are tied on by dragging them to a point; "Joined by"
                            turns a rope into a rod. */}
                        <button
                            type="button"
                            className="mbench-tile"
                            data-active={phone && link ? 'true' : undefined}
                            title="Put a rope on the bench"
                            onClick={addRope}
                        >
                            <PartIcon kind="connector" />
                            <span>Connector</span>
                        </button>
                    </div>
                    {/* A phone: the picked part's settings open here, under the
                        tiles, beside what made it — not in the runtime pane. */}
                    {phone && (partBox || linkBox) && (
                        <div className="mbench-partprops">
                            {selected && (() => {
                                const same = world.parts.filter(p => p.kind === selected.kind);
                                return same.length > 1 && (
                                    <div className="mbench-partpick" role="group" aria-label={`Which ${defOf(selected).label.toLowerCase()}`}>
                                        {same.map(p => (
                                            <button key={p.id} type="button" aria-pressed={p.id === selected.id}
                                                onClick={() => { setSelected(p.id); setSelectedLink(null); }}>
                                                {defOf(p).name(p as never, world)}
                                            </button>
                                        ))}
                                    </div>
                                );
                            })()}
                            {link && world.links.length > 1 && (
                                <div className="mbench-partpick" role="group" aria-label="Which connector">
                                    {world.links.map((l, i) => (
                                        <button key={l.id} type="button" aria-pressed={l.id === link.id}
                                            onClick={() => { setSelectedLink(l.id); setSelected(null); }}>
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {partBox ?? linkBox}
                        </div>
                    )}
                </RailGroup>

                {/* The ONE control for joining: it starts it and it is the way
                    out. A second Cancel in the prompt bar was two buttons for
                    one piece of state. */}
                <button
                    type="button"
                    className="mbench-join"
                    aria-pressed={joining}
                    onClick={() => { setJoining(j => !j); setFirst(null); }}
                >{joining ? 'Stop joining' : 'Join two parts'}</button>

                {/* The bench itself, not a part of it. Gravity has always been
                    a field on the world and nothing ever let a reader touch it;
                    bounce is new. Both belong on the left, with the things you
                    build with, because neither is about the one part you have
                    selected. */}
                <RailGroup title="The bench" open={false} fixed={phone}>
                    <Slider
                        label="Gravity" unit="m/s²" min={0} max={25} step={0.1} noteInline
                        value={world.gravity}
                        note={world.gravity === 9.8 ? 'Earth' : world.gravity === 1.6 ? 'the Moon' : undefined}
                        onChange={v => edit(w => ({ ...w, gravity: v }))}
                    />
                    {/* Contacts only. A rope that bounced would be a bungee, and
                        1.00 never settles at all, so the slider stops short. */}
                    <Slider
                        label="Bounce" unit="e" min={0} max={0.9} step={0.05} noteInline
                        value={world.restitution ?? 0}
                        note={(world.restitution ?? 0) === 0 ? 'dead landings' : `keeps ${(((world.restitution ?? 0) ** 2) * 100).toFixed(0)}% of the height`}
                        onChange={v => edit(w => ({ ...w, restitution: v }))}
                    />
                </RailGroup>

                <RailGroup title={setups.enabled ? 'Setups' : 'Try one'} className="mbench-wide-only">
                    <select value={setups.current ? SAVED + setups.current.id : presetKey} onChange={e => pick(e.target.value)}
                        aria-label={setups.enabled ? 'Open a saved setup or a textbook arrangement' : 'Load a textbook arrangement'}>
                        <option value="">— your own build —</option>
                        {setups.enabled ? (
                            <>
                                {/* The open one is listed even before the list arrives, so the select can show it. */}
                                <optgroup label="My setups">
                                    {setups.current && !setups.mine.some(m => m.id === setups.current?.id) && (
                                        <option value={SAVED + setups.current.id}>{setups.current.name}</option>
                                    )}
                                    {setups.mine.map(m => <option key={m.id} value={SAVED + m.id}>{m.name}</option>)}
                                    {setups.mine.length === 0 && !setups.current && <option disabled value="-">Nothing saved yet</option>}
                                </optgroup>
                                <optgroup label="Textbook">
                                    {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                                </optgroup>
                            </>
                        ) : PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    {savedNotes && <p className="mbench-claim">{savedNotes}</p>}
                    {presetKey && (
                        <p className="mbench-claim">
                            {/* Every preset arrives held, so its claim is about a
                                bench that has not been let go of yet. Say which
                                one, rather than leaving a reader to compare a
                                number on the page with a different number on the
                                bench and wonder which is wrong. */}
                            {anyPinned(world) && <><b>Let it go, and:</b> </>}
                            {PRESETS.find(p => p.key === presetKey)?.claim}
                        </p>
                    )}
                </RailGroup>
            </div>

            <div className="mbench-main">
                {/* One row: the setup pane's switch at the left edge, the name
                    in the MIDDLE of the bench, and at the right the runtime
                    pane's switch. Three columns, so the
                    name is centred on the bench whatever sits either side. */}
                <BenchPhoneHeader
                    title={shownTitle + (unsaved ? ' •' : '')}
                    trailing={setups.enabled ? (
                        <button type="button" className="bench-phone-header-btn" onClick={saveNow}
                            disabled={setups.busy || (!!setups.current && !unsaved)}
                            title={setups.current ? 'Save the changes' : 'Save to My setups'} aria-label="Save">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M5 3h11l3 3v15H5z" /><path d="M8 3v5h7V3" /><path d="M8 21v-7h8v7" />
                            </svg>
                        </button>
                    ) : undefined}
                    back={onBack ? { label: 'Back', onClick: onBack } : undefined}
                    theme={theme}
                    onToggleTheme={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
                    fullscreen={fullscreen}
                />
                <div className="mbench-top">
                    <div className="mbench-top-left">
                        {/* A phone's way out: the rail that holds it wider is a tab here. */}
                        {onBack && <button type="button" className="mbench-iconbtn mbench-phone-only" onClick={onBack} title="Back" aria-label="Back">←</button>}
                        <button type="button" className="mbench-panetoggle mbench-wide-only" aria-pressed={showRail} onClick={() => setShowRail(v => !v)} title={showRail ? 'Hide the setup pane' : 'Show the setup pane'} aria-label={showRail ? 'Hide the setup pane' : 'Show the setup pane'}>◧</button>
                        {setups.enabled && (
                            <>
                                <button type="button" className="mbench-savebtn" onClick={saveNow}
                                    disabled={setups.busy || (!!setups.current && !unsaved)}
                                    title={setups.current ? `Save the changes to “${setups.current.name}” (⌘S)` : 'Save this arrangement to My setups (⌘S)'}>
                                    {setups.busy ? 'Saving…' : setups.current && !unsaved ? 'Saved' : 'Save'}
                                </button>
                                {setups.current && (
                                    <button type="button" className="mbench-savebtn mbench-wide-only" onClick={() => setSaveDialog('copy')}
                                        disabled={setups.busy} title="Save this arrangement as a new setup, leaving the open one as it was">Save as…</button>
                                )}
                                {setups.error && !saveDialog && <span className="mbench-saveerr" role="alert">Not saved: {setups.error}</span>}
                            </>
                        )}
                    </div>
                    <h2>
                        {shownTitle}
                        {unsaved && <span className="mbench-unsaved" title="Changed since it was saved"> •</span>}
                    </h2>
                    <div className="mbench-top-right">
                        <button
                            type="button"
                            className="mbench-panetoggle mbench-fullscreen mbench-wide-only"
                            aria-pressed={fullscreen.isFullscreen}
                            onClick={fullscreen.toggle}
                            title={fullscreen.isFullscreen ? 'Leave full screen' : 'Fill the screen with the bench'}
                            aria-label={fullscreen.isFullscreen ? 'Leave full screen' : 'Full screen'}
                        >
                            <FullscreenIcon exiting={fullscreen.isFullscreen} />
                        </button>
                        <button type="button" className="mbench-panetoggle mbench-wide-only" aria-pressed={showProps} onClick={() => setShowProps(v => !v)} title={showProps ? 'Hide the runtime pane' : 'Show the runtime pane'} aria-label={showProps ? 'Hide the runtime pane' : 'Show the runtime pane'}>◨</button>
                    </div>
                </div>
                {!phone && joinHint}

                <div className="mbench-stage bench-accent-wash">
                    {/* On the bench, not in the rail: what the drawing shows is
                        a property of the drawing, and the switch belongs where
                        the thing being switched is. */}
                    {/* ── the run, on the bench ────────────────────────────────
                        Play, step, reset, the scrubber and the clock, at the top
                        right of the room just under its ceiling — on the thing
                        they run, not in a title bar above it — with the axes the
                        numbers are signed by directly beneath them.

                        Placed as a share of the stage's height: the scene always
                        fills the stage, is always TALL metres high, and starts
                        ROOM_ABOVE_CEILING above the ceiling, so the ceiling is at a fixed
                        fraction of the way down whatever size the window is. */}
                    <div
                        className="mbench-runbar"
                        style={phone ? undefined : { top: `max(4px, calc(${ceilingPct / 2}% - 21px))` }}
                    >
                        <div className="mbench-transport">
                        <button
                            type="button" onClick={stepBack} className="mbench-run-extra"
                            disabled={!runnable || duration <= 0 || t <= 0}
                            aria-label="Step back 0.2 s"
                            title="Step back 0.2 s"
                        >⏮</button>
                        <button
                            type="button" onClick={play}
                            disabled={!anyPinned(world) && (!runnable || duration <= 0)}
                            aria-label={playing ? 'Pause' : anyPinned(world) ? 'Pull the pins' : 'Play'}
                            title={playing ? 'Pause' : anyPinned(world) ? 'Pull every pin out and let it go' : 'Play'}
                        >{playing ? '⏸' : '▶'}</button>
                        {/* One step, then stop. The thing a reader needs to
                            debug a motion: a run is a chain of arrangements
                            and this walks it one readable interval at a time,
                            with every force and every number in the panes
                            settling on each. Pulls the pins first if any are
                            in, so it starts the same way ▶ does. */}
                        <button
                            type="button" onClick={step} className="mbench-run-extra"
                            disabled={!anyPinned(world) && (!runnable || duration <= 0)}
                            aria-label="Step forward 0.2 s"
                            title="Step forward 0.2 s and pause"
                        >⏭</button>
                        {/* A scrubber, not a clock: the run is worked out
                            before it is shown, so any instant costs the same as
                            the next one. Dragging back and forth over the
                            moment a weight lands is the whole point. */}
                        <input
                            className="mbench-scrub"
                            type="range" min={0} max={Math.max(duration, 0.001)} step={0.001}
                            value={Math.min(t, duration)}
                            disabled={!runnable || duration <= 0}
                            onChange={e => { setPlaying(false); setT(+e.target.value); }}
                            aria-label="Time"
                        />
                        <span className="mbench-clock">{t.toFixed(2)} s</span>
                        <button
                            type="button" onClick={rewind}
                            aria-label="Back to the start"
                            title={pulled.length ? 'Back to the start, and the pins go back in' : 'Back to the start'}
                        >↺</button>
                        {/* Slow motion. A block that lands in a third of a
                            second is over before a reader has seen it move. */}
                        <select
                            className="mbench-rate mbench-run-extra"
                            value={rate}
                            onChange={e => setRate(+e.target.value)}
                            aria-label="Playback speed"
                            title="Playback speed"
                        >
                            {Array.from({ length: 10 }, (_, k) => (k + 1) / 10).map(r => (
                                <option key={r} value={r}>{r.toFixed(1)}×</option>
                            ))}
                        </select>
                        </div>
                        {/* The view switches, in one row with the axes they are read
                            against, under the run controls. */}
                        <div className="mbench-runrow">
                        <div className="mbench-stagetools">
                            <button
                                type="button" aria-pressed={showForces}
                                title="Every force on the bench, and the tension of whatever is selected"
                                onClick={() => setShowForces(v => !v)}
                            >Forces</button>
                            <button type="button" aria-pressed={showMotion} onClick={() => setShowMotion(v => !v)}>Motion</button>
                        </div>
                        {/* A phone: framing the view, on the bench. Light/dark and
                            full screen are in the title bar. */}
                        <div className="mbench-phone-only mbench-viewtools">
                            <button type="button" onClick={() => setFitKey(k => k + 1)}
                                title="Frame the whole machine" aria-label="Frame the whole machine">Fit</button>
                        </div>
                            {/* The way the wall's ruler counts — see `ys`. */}
                            <svg className="mbench-runaxes" viewBox="-4 -4 40 40" width="40" height="40" aria-label={ys < 0 ? 'x to the right, y up' : 'x to the right, y down'}>
                                {ys < 0 ? (
                                    <>
                                        <line x1={0} y1={24} x2={22} y2={24} />
                                        <path d="M 24 24 L 18 20.5 L 18 27.5 Z" />
                                        <line x1={0} y1={24} x2={0} y2={2} />
                                        <path d="M 0 0 L -3.5 6 L 3.5 6 Z" />
                                        <text x={29} y={25} dominantBaseline="middle">x</text>
                                        <text x={7} y={3} dominantBaseline="middle">y</text>
                                    </>
                                ) : (
                                    <>
                                        <line x1={0} y1={0} x2={22} y2={0} />
                                        <path d="M 24 0 L 18 -3.5 L 18 3.5 Z" />
                                        <line x1={0} y1={0} x2={0} y2={22} />
                                        <path d="M 0 24 L -3.5 18 L 3.5 18 Z" />
                                        <text x={29} y={1} dominantBaseline="middle">x</text>
                                        <text x={0} y={33} textAnchor="middle">y</text>
                                    </>
                                )}
                            </svg>
                        </div>
                    </div>
                    <MechanicsScene2D
                        /* Only with MOTION on — it is what that switch shows — and
                           not with Forces on as well: the force tags sit in the
                           same place, and a card of speeds on top of them hid the
                           forces that switch was turned on to show. */
                        motionTags={showMotion && !showForces ? motionTags : []}
                        inMotion={watching}
                        forceTags={forceTags}
                        ghost={ghost}
                        svgRef={svg}
                        world={ghost?.preview ?? reshaped ?? shown}
                        force={atRest ? eq.force : (runnable && now ? now.force : null)}
                        motion={motion}
                        speed={speed}
                        arrows={arrows}
                        showForces={showForces}
                        problems={onShow}
                        selectedId={selectedId}
                        first={first}
                        joining={joining}
                        offered={offered}
                        picking={!!joinFrom}
                        selectedLinkId={selectedLink}
                        onLinkClick={id => { if (!joining) { setSelectedLink(id); setSelected(null); if (phone) setSheet('parts'); } }}
                        onPartPointerDown={onPartPointerDown}
                        onPartClick={id => { if (id !== resizing) setResizing(null); if (!joining) { setSelected(id); setSelectedLink(null); if (phone) setSheet('parts'); } }}
                        onPartDoubleClick={onPartDoubleClick}
                        /* A phone pinches and pans, gives joining points and handles
                           a finger's target, and takes a long press for a double-click. */
                        zoom={phone ? zoom : null}
                        onZoom={phone ? setZoom : undefined}
                        touchPx={phone ? 14 : undefined}
                        onPartLongPress={phone ? onPartDoubleClick : undefined}
                        fitKey={phone ? fitKey : undefined}
                        resizing={!watching && !joining ? resizing : null}
                        onHandlePointerDown={onHandlePointerDown}
                        onPort={onPort}
                        onTailPointerDown={onTailPointerDown}
                        pulling={pulling}
                        onPortPointerDown={onPortPointerDown}
                        onRopeEndPointerDown={watching ? undefined : onRopeEndPointerDown}
                        onBackground={() => { setResizing(null); if (!joining) { setSelected(null); setSelectedLink(null); } }}
                    />

                </div>

                {/* Nothing is drawn under the bench. The "not moved yet" note and
                    the list of events used to sit here, below the floor, and
                    they have other places to live. */}

            </div>

            {/* ── the properties pane ──────────────────────────────────────
              *
              * Everything about the ONE thing a reader has picked: what it is,
              * what can be changed about it, and what it is doing. On the right
              * and not in the left rail because the left rail is what you build
              * WITH — a tray of parts and a list of arrangements — and this is
              * about the bench you have already built. Mixing the two put
              * "Remove this part" directly under "Weight", which is a tray that
              * changes meaning depending on what you last clicked.
              */}
            <aside className="mbench-props">
                {/* Light or dark at the left, close at the right — one row,
                    kept at the top as the pane scrolls. */}
                <div className="mbench-panehead">
                    <ThemeToggle theme={theme} onToggle={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))} />
                    <CloseButton onClick={() => setShowProps(false)} label="Hide the runtime pane" />
                </div>

                {/* What is on the bench comes FIRST, then the one thing selected
                    from it. Edit config and the free-body diagram are about that
                    one part, so they read as its detail only after the list it
                    was picked from — and a click in the list lands just below. */}
                {stopper ? (
                    <>
                    {/* The PARTS are still there when the numbers are not.
                        The list used to be read off the run, so a bench
                        that could not be run listed nothing at all — and
                        that is exactly the bench where a reader most wants
                        to see what is on it and click into the part that
                        is wrong. Names, masses and what holds each still;
                        no speeds or forces, because nothing was solved. */}
                    <RailGroup title="Parts and connectors" open={false}>
                        <div className="mbench-tablewrap">
                            <table className="mbench-table mbench-parts">
                                <thead><tr><th>Part</th><th>Key property</th><th>Speed</th><th>Accel.</th></tr></thead>
                                <tbody>
                                    {bodies.map(body => {
                                        const row = partRow(world, body.partIds);
                                        const free = isFree(body);
                                        return (
                                            <tr
                                                key={body.id}
                                                className={body.id === selectedBody?.id ? 'is-sel' : undefined}
                                                onClick={() => { setSelected(body.partIds[0] ?? null); setSelectedLink(null); }}
                                            >
                                                <td>{named(row.names)}</td>
                                                <td>{lines(row.keys)}</td>
                                                <td>—</td>
                                                <td>{free ? '—' : body.partIds.some(id => partById(world, id)?.pinned) ? 'pinned' : 'fixed'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {connectorsTable(false)}
                    </RailGroup>
                    </>
                ) : (
                    <>
                    {/* What is on the bench, parts and the joins between
                        them, in one place and first: it is the index
                        every other table below refers back to. */}
                    <RailGroup title="Parts and connectors" open={false}>
                        <div className="mbench-tablewrap">
                            <table className="mbench-table mbench-parts">
                                <thead><tr><th>Part</th><th>Key property</th><th>Speed</th><th>Accel.</th></tr></thead>
                                <tbody>
                                    {(now?.freeBodies ?? []).map(fb => {
                                        const body = run?.bodies.find(b => b.id === fb.bodyId);
                                        const row = body ? partRow(world, body.partIds) : { names: [[fb.name, '']] as [string, string][], keys: ['—'] };
                                        const free = !!body && isFree(body);
                                        /* Signed by where it is going: DOWN is positive, as
                                           everywhere else on this bench, and a body going
                                           neither up nor down is signed rightward. Reading
                                           `[0]` was the sideways number, not "the" one. */
                                        const one = (per: Record<string, number[]> | undefined): number => {
                                            if (!body) return 0;
                                            const u = inBench(body, (per ?? {})[fb.bodyId]);
                                            const way = Math.abs(u.y) > 1e-9 ? Math.sign(u.y * ys) : Math.sign(u.x) || 1;
                                            return u.size * way;
                                        };
                                        const a2 = one(now?.acceleration);
                                        const v2 = one(now?.v);
                                        return (
                                            <tr
                                                key={fb.bodyId}
                                                className={fb.bodyId === selectedBody?.id ? 'is-sel' : undefined}
                                                onClick={() => { setSelected(body?.partIds[0] ?? null); setSelectedLink(null); }}
                                            >
                                                <td>{named(row.names)}</td>
                                                <td>{lines(row.keys)}</td>
                                                {/* Pinned and fixed are different answers to
                                                    "why is this not moving", and the reader is
                                                    owed the difference: one of them goes away
                                                    when you press play. */}
                                                <td>{free ? `${fixed(v2, 2)}` : ''}</td>
                                                <td>{free ? `${fixed(a2, 2)}` : fb.pinned ? 'pinned' : 'fixed'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {connectorsTable(true)}
                    </RailGroup>
                    </>
                )}

                {!selected && !link && (
                    <p className="mbench-nothing">
                        Click a part or a rope on the bench to see what it is, change it, and read
                        what it is carrying.
                    </p>
                )}

                {!phone && partBox}

                {!phone && linkBox}

                {/* ── what the bench works out ─────────────────────────────
                  *
                  * Under the selection rather than across the foot of the
                  * bench. Three tables of three columns each is a band of
                  * numbers as wide as the drawing and twice as loud, and none
                  * of it changes while you build — so it belongs beside the
                  * bench, folded, where it can be opened when it is wanted.
                  * Collapsed, the whole answer is three lines. */}
                <div className="mbench-answer" aria-live="polite">
                    {stopper ? (
                        <>
                            <p className="mbench-nothing">Nothing to work out yet — see the note at the top left.</p>
                        </>
                    ) : (
                        <>

                            {/* ── what is moving, while it moves ─────────────
                                The masses only, and only while the bench is a
                                machine. "Parts and connectors" lists the wheels
                                and the hooks too, which is the right table for
                                checking an arrangement and the wrong one for
                                watching a run: a reader following a machine
                                wants the weights, their speed and how fast
                                that is changing, and nothing else in the way. */}
                            {!atRest && now && runnable && (
                                <RailGroup title="In motion">
                                    <div className="mbench-tablewrap">
                                        <table className="mbench-table">
                                            <thead><tr><th>Mass</th><th>Speed</th><th>Accel.</th></tr></thead>
                                            <tbody>
                                                {world.parts.filter(p => p.kind === 'weight').map(p => {
                                                    const body = run?.bodies.find(b => b.partIds.includes(p.id));
                                                    if (!body || !isFree(body)) return null;
                                                    /* Down is positive, as everywhere on this bench;
                                                       a body going neither up nor down is signed
                                                       rightward. */
                                                    const one = (per: Record<string, number[]> | undefined): number => {
                                                        const u = inBench(body, (per ?? {})[body.id]);
                                                        const way = Math.abs(u.y) > 1e-9 ? Math.sign(u.y * ys) : Math.sign(u.x) || 1;
                                                        return u.size * way;
                                                    };
                                                    return (
                                                        <tr
                                                            key={p.id}
                                                            className={p.id === selectedId ? 'is-sel' : undefined}
                                                            onClick={() => { setSelected(p.id); setSelectedLink(null); }}
                                                        >
                                                            <td>{defOf(p).name(p as never, world)}
                                                                <em> {kg((p.data as { mass: number }).mass)}</em></td>
                                                            <td>{fixed(one(now.v), 2)} m/s</td>
                                                            <td>{fixed(one(now.acceleration), 2)} m/s²</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </RailGroup>
                            )}

                            {/* ── the variables ──────────────────────────
                                What the bench was actually asked to find, and
                                what each one came to. A reader checking the
                                arithmetic needs the LIST first: how many
                                unknowns there are, what each one is, and that
                                the count matches the equations. Without it the
                                bench hands over an answer with no way to tell
                                whether it was the answer to the right question. */}
                            <RailGroup title="Variables">
                                {/* These are the SETUP's unknowns. Once the
                                    clock is running, "no arrangement balances
                                    these" is an answer to the question the
                                    reader left behind when they pressed play —
                                    so while watching, the pane says which
                                    question it is the answer to and stops
                                    calling the run a fault. */}
                                <p className="mbench-claim">
                                    {watching
                                        ? `${eq.unknowns.length} unknowns — what the bench was asked before it started moving.`
                                        : eq.solved
                                            ? `${eq.unknowns.length} unknowns, solved in ${eq.steps} steps.`
                                            : eq.fault?.adrift.length
                                                ? `No arrangement balances these: ${eq.fault.adrift.map(a => a.name).join(', ')} cannot stand still.`
                                                : `${eq.unknowns.length} unknowns — no answer.`}
                                </p>
                                <div className="mbench-tablewrap">
                                    <table className="mbench-table">
                                        <thead><tr><th>Unknown</th><th>Value</th></tr></thead>
                                        <tbody>
                                            {vectors(eq.unknowns, ys, world.room.floor, world.room.ceiling).map(v => (
                                                <tr key={v.id} data-kind={v.kind}>
                                                    <td>{v.label}</td>
                                                    <td>{eq.solved ? v.text : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Named, never quoted. What a failed solve
                                    leaves on a part is how close it could get
                                    to an answer that does not exist, and that
                                    is not a force anything is carrying — least
                                    of all on a massless pulley, where ΣF = 0
                                    is a law rather than an outcome. */}
                                {(watching ? [] : eq.fault?.adrift ?? []).map(a => (
                                    <p key={a.partId} className="mbench-claim">
                                        {a.name} cannot be balanced where it stands.
                                    </p>
                                ))}
                            </RailGroup>

                        </>
                    )}
                </div>

                    {/* LAST in the pane, whether or not the bench has an answer:
                    it is for reporting what the bench did, and a bench that
                    cannot be solved is the one most worth reporting. */}
                {/* A report you can paste.
                        Every bug on this bench has been found by
                        someone describing what they saw and the two of
                        us then guessing at the arrangement behind it.
                        This is the arrangement itself — where every
                        part is, what holds it, and what the statics
                        makes of it — so a report carries the bench
                        with it rather than a description of one. */}
                    {/* Not on the public site. It is for reporting what the
                        bench did wrong, and a reader on zitmus.com has no
                        one to paste it to. MODE, not PROD: the dev site is
                        a production BUILD made with `--mode dev`, and it is
                        exactly where these reports come from. */}
                    {import.meta.env.MODE !== 'production' && (
                    <RailGroup title="Debug report" open={false}>
                        {/* One row: they are three ways to hand over the same bench. */}
                        <div className="mbench-copyrow">
                        <button
                            type="button"
                            className="mbench-copy"
                            onClick={() => { void navigator.clipboard?.writeText(report); }}
                        >Copy</button>
                        {/* The bench itself, exactly: every part with its data
                            and every join with its path and length, as the
                            engine holds them. The report above is written for
                            reading and cannot be loaded back; this can, so a
                            bench that misbehaves can be run again as it was. */}
                        <button
                            type="button"
                            className="mbench-copy"
                            onClick={() => {
                                void navigator.clipboard?.writeText(JSON.stringify(world, null, 1))
                                    .then(() => { setBenchCopied(true); window.setTimeout(() => setBenchCopied(false), 1500); });
                            }}
                        >{benchCopied ? 'Copied' : 'Copy the bench'}</button>
                        {/* Sent, not copied: the bench and the report go to the
                            logs under an id, so a fault can be looked up there
                            rather than pasted. */}
                        {onReport && (
                            <button
                                type="button"
                                className="mbench-copy"
                                disabled={reported === 'sending'}
                                onClick={() => {
                                    setReported('sending');
                                    onReport({ bench: 'mechanics', world, report })
                                        .then(id => setReported(`Reported — ${id}`))
                                        .catch(() => setReported('Could not report'));
                                }}
                            >{reported === 'sending' ? 'Reporting…' : 'Report'}</button>
                        )}
                        </div>
                        {reported && reported !== 'sending' && <p className="mbench-reported">{reported}</p>}
                        <pre className="mbench-report">{report}</pre>
                    </RailGroup>
                    )}
            </aside>

            {/* A phone's arrangements: a list to tap, each with what it shows. */}
            <div className="mbench-presets">
                {setups.enabled && setups.mine.length > 0 && (
                    <>
                        <h3 className="mbench-grouphead">My setups</h3>
                        <ul>
                            {setups.mine.map(m => (
                                <li key={m.id}>
                                    <button type="button" aria-pressed={setups.current?.id === m.id} onClick={() => void loadSaved(m.id)}>{m.name}</button>
                                    {setups.current?.id === m.id && savedNotes && <p className="mbench-claim">{savedNotes}</p>}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <h3 className="mbench-grouphead">Try one</h3>
                <ul>
                    {PRESETS.map(p => (
                        <li key={p.key}>
                            <button type="button" aria-pressed={presetKey === p.key} onClick={() => loadPreset(p.key)}>{p.label}</button>
                            {/* What the loaded one should do, under it — at the foot of
                                the list it read as a note about the list. */}
                            {presetKey === p.key && (
                                <p className="mbench-claim">
                                    <b>{anyPinned(world) ? 'Let it go, and:' : 'What it does:'}</b> {p.claim}
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            {/* A phone's panes, one showing at a time, docked under the bench.
                Hidden wider, where every pane has a column. */}
            <nav className="mbench-sheetbar mbench-phone-only" aria-label="Panes">
                <button type="button" aria-pressed={sheet === 'parts'} onClick={() => setSheet('parts')}>
                    Parts
                    {/* The problems are listed at the top of Parts; say they are there. */}
                    {onShow.length > 0 && <b className="mbench-sheetbadge" aria-label={`${onShow.length} to fix`}>{onShow.length}</b>}
                </button>
                <button type="button" aria-pressed={sheet === 'results'} onClick={() => setSheet('results')}>Results</button>
                <button type="button" aria-pressed={sheet === 'presets'} onClick={() => setSheet('presets')}>{setups.enabled ? 'Setups' : 'Try one'}</button>
            </nav>

            {saveDialog && (
                <SaveSetupDialog
                    heading={saveDialog === 'copy' ? 'Save as a new setup' : 'Save to My setups'}
                    initialName={saveDialog === 'copy' && setups.current ? `${setups.current.name} (copy)`
                        : PRESETS.find(p => p.key === presetKey)?.label ?? 'My mechanics setup'}
                    initialNotes={saveDialog === 'copy' ? setups.current?.notes : undefined}
                    chapters={BENCH_CHAPTER_OPTIONS.mechanics}
                    initialChapterIds={saveDialog === 'copy' ? setups.current?.chapterIds : undefined}
                    busy={setups.busy}
                    error={setups.error}
                    onCancel={() => setSaveDialog(null)}
                    onSave={(name, notes, chapterIds) => void saveFromDialog(name, notes, chapterIds)}
                />
            )}
        </div>
    );
}

/**
 * One body, on its own, with every force acting on it named — and `ΣF = ma`
 * written underneath.
 *
 * This is the line of working a student is asked for, so the bench shows the
 * same one: not the multipliers the solver found, but the forces a reader
 * would have drawn, added up.
 */
/**
 * One body, on its own, with every force named — the picture, the numbers and
 * the equations, all from ONE list of terms.
 *
 * That last part is the whole shape of this component, and it was learned the
 * hard way. The arrows, the table, the sums and the sentence underneath were
 * four separate readings of the same body, each written when every value was a
 * number; when forces that nobody had worked out arrived, each reading was
 * taught about them separately and each missed something. The bench ended up
 * printing `ΣF = 0.0` on one line and `T = ?` two lines above it.
 *
 * So: build the terms once — the forces, plus whatever holds it — and note
 * which of them have a size. Everything below is a view of that list, and the
 * four views cannot disagree because there is nothing for them to disagree
 * about.
 */
/**
 * Where to draw each of a diagram's arrows so two never lie on one another.
 *
 * Arrows start where their force acts, and forces that act in the same
 * direction along the same line — a rope and the floor both holding a block up
 * through its middle — were drawn as one line with two labels written over
 * each other. Parallel arrows from one point are spread side by side, across
 * their shared direction, and each is labelled on its own outside edge.
 *
 * Takes each arrow's start and its vector (sketch units, y down); gives back
 * the sideways shift for its start and which side its label goes (+1 / −1 along
 * the arrow's left-hand normal, or 0 where it is alone and the caller picks).
 */
function spreadParallel(arrows: ({ ax: number; ay: number; dx: number; dy: number } | null)[], gap: number): { ox: number; oy: number; side: number }[] {
    const out = arrows.map(() => ({ ox: 0, oy: 0, side: 0 }));
    const done = new Set<number>();
    arrows.forEach((a, i) => {
        if (!a || done.has(i)) return;
        const len = Math.hypot(a.dx, a.dy);
        if (len < 1e-9) return;
        const ux = a.dx / len, uy = a.dy / len;
        const group = [i];
        arrows.forEach((b, j) => {
            if (!b || j <= i || done.has(j)) return;
            const lb = Math.hypot(b.dx, b.dy);
            if (lb < 1e-9) return;
            const same = (b.dx / lb) * ux + (b.dy / lb) * uy > 0.985;
            /* On the same LINE, wherever along it each starts: a push down on
               a block's top and its weight down from its middle overlap as
               surely as two arrows from one point. */
            const near = Math.abs((b.ax - a.ax) * uy - (b.ay - a.ay) * ux) < gap * 0.6;
            if (same && near) group.push(j);
        });
        if (group.length < 2) return;
        // The left-hand normal of the shared direction.
        const nx = -uy, ny = ux;
        group.forEach((k, slot) => {
            done.add(k);
            const shift = (slot - (group.length - 1) / 2) * gap;
            out[k] = { ox: nx * shift, oy: ny * shift, side: shift >= 0 ? 1 : -1 };
        });
    });
    return out;
}

function FreeBody({ fb, adrift, ys = 1 }: { fb: M.FreeBody; adrift?: { x: number; y: number }; ys?: number }): React.JSX.Element {
    /** A force on this body: a direction always, a size only sometimes. */
    interface Term {
        label: string;
        kind: string;
        /** Newtons when known; a unit vector when not. */
        vector: { x: number; y: number };
        /** True when `vector` is a direction and the size is nobody's answer. */
        unknown: boolean;
        /**
         * Unknown in BOTH components, not merely in size.
         *
         * A rope's pull has a direction the geometry settles, so its components
         * are fixed multiples of one unknown — `−T`, `0.50 T`. A PIN's reaction
         * has no such line: it supplies whatever is needed in whatever
         * direction, so it is two separate unknowns and has to be written that
         * way. Treating it as one gave `ΣFx = R = 0`, which reads as though the
         * sum of the forces were the reaction.
         */
        free: boolean;
        /** What to call it while it has no number: T, N, f, R. */
        symbol: string;
    }

    const held = !fb.hold && (fb.forces.some(f => f.unknown) || fb.pinned !== undefined);
    const terms: Term[] = [
        ...fb.forces.map(f => ({
            label: f.label, kind: f.kind, vector: f.vector, unknown: !!f.unknown, free: false, symbol: symbolOf(f),
        })),
        ...(fb.hold
            ? [{ label: fb.pinned ? 'the pin' : 'what holds it', kind: 'normal', vector: fb.hold, unknown: false, free: false, symbol: 'R' }]
            : held
                ? [{ label: fb.pinned ? 'the pin' : 'what holds it', kind: 'normal', vector: { x: 0, y: 0 }, unknown: true, free: true, symbol: 'R' }]
                : []),
    ];
    const vague = terms.some(t => t.unknown);

    /* The longest arrow fills the box, whatever the forces happen to be: a
       diagram drawn to a fixed newtons-per-pixel is unreadable at 5 N and off
       the page at 500. What a reader takes from it is the DIRECTIONS and which
       arrow is longer, and both survive the scaling. An unknown is drawn at a
       middling length — it has a direction to show and no size to compare. */
    const S = 110;
    const known = terms.filter(t => !t.unknown).map(t => Math.hypot(t.vector.x, t.vector.y));
    const biggest = Math.max(1e-6, ...known);
    /* Every arrow the same length — a direction; the sizes are in the table.
       See PartFBD. */
    const arrow = (t: Term): { x: number; y: number } => {
        const n = Math.hypot(t.vector.x, t.vector.y);
        if (n < 1e-12 || (!t.unknown && n < biggest * 1e-3)) return { x: 0, y: 0 };
        // The sketch is an SVG, y down; the bench's forces are y up.
        return { x: (t.vector.x / n) * 64, y: (-t.vector.y / n) * 64 };
    };

    /** One equation, written the way a reader would: numbers where there are
     *  numbers, letters where there are not, and always `= 0` when it is held. */
    const line = (axis: 'x' | 'y'): string => {
        const k = axis === 'y' ? ys : 1;
        const bits = terms
            .map(t => (t.free ? `${t.symbol}${axis}`
                : Math.abs(t.vector[axis]) < 1e-9 ? null
                    : t.unknown ? times(t.vector[axis] * k, t.symbol) : fixed(t.vector[axis] * k, 1)))
            .filter(Boolean) as string[];
        if (!bits.length) return '0.0 = 0';
        const body = bits.map((b, i) => (i === 0 ? b : b.startsWith('−') ? `− ${b.slice(1)}` : `+ ${b}`)).join(' ');
        if (vague) return `${body} = 0`;
        const total = terms.reduce((a, t) => a + t.vector[axis] * k, 0);
        return `${body} = ${fixed(total, 1)} N`;
    };

    return (
        <div className="mbench-fbd">
            <h3>{fb.name} on its own</h3>
            <svg viewBox={`${-S} ${-S} ${S * 2} ${S * 2}`} style={{ width: S * 2, maxWidth: '100%', height: 'auto' }}
                className="mbench-fbd-svg" role="img" aria-label={`Free-body diagram for ${fb.name}`}>
                <Body kind={fb.kind} along={(v => (v ? { x: v.x, y: -v.y } : undefined))(fb.forces.find(f => Math.hypot(f.vector.x, f.vector.y) > 1e-9)?.vector)} />
                {(() => {
                    const drawn = terms.map(t => {
                        const { x: dx, y: dy } = arrow(t);
                        return Math.hypot(dx, dy) < 0.5 ? null : { ax: 0, ay: 0, dx, dy };
                    });
                    const spread = spreadParallel(drawn, 18);
                    return terms.map((t, i) => {
                        const a = drawn[i];
                        if (!a) return null;
                        const { ox, oy, side } = spread[i];
                        const len = Math.hypot(a.dx, a.dy);
                        const nx = -a.dy / len, ny = a.dx / len;
                        /* Alone, the label sits past the tip as before; one of
                           a parallel pair is named beside its own shaft, on the
                           outside, so the two names do not share a line. */
                        const lx = side ? ox + a.dx * 0.6 + nx * side * 8 : a.dx * 1.18;
                        const ly = side ? oy + a.dy * 0.6 + ny * side * 8 : a.dy * 1.18;
                        const sideX = nx * side;
                        return (
                            <g key={i} className="mbench-fbd-arrow" data-kind={t.kind} data-unknown={t.unknown || undefined}>
                                <line x1={ox} y1={oy} x2={ox + a.dx} y2={oy + a.dy} markerEnd="url(#pb-arrow)" />
                                <text x={lx} y={ly}
                                    textAnchor={!side ? 'middle' : sideX > 0.3 ? 'start' : sideX < -0.3 ? 'end' : 'middle'}
                                    dominantBaseline="middle">{onArrow(t.label)}</text>
                            </g>
                        );
                    });
                })()}
                <defs>
                    <marker id="pb-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                    </marker>
                </defs>
            </svg>

            <table className="mbench-fbdtable">
                <tbody>
                    {terms.map((t, i) => (
                        <tr key={i} data-kind={t.kind} data-unknown={t.unknown || undefined}>
                            <td>{t.label}</td>
                            {/* An unknown force still ACTS, and its DIRECTION is
                                known — only the size is not. The components are
                                written as multiples of the unknown, which keeps
                                the ratio between them: a rope at 30° gives
                                −0.50 T and −0.87 T, not two shrugs. */}
                            <td>{t.free ? `${t.symbol}x` : t.unknown ? times(t.vector.x, t.symbol) : fixed(t.vector.x, 1)}</td>
                            <td>{t.free ? `${t.symbol}y` : t.unknown ? times(t.vector.y * ys, t.symbol) : fixed(t.vector.y * ys, 1)}</td>
                        </tr>
                    ))}
                    <tr className="is-sum">
                        <td>ΣF</td>
                        <td>{vague ? '0' : fixed(terms.reduce((a, t) => a + t.vector.x, 0), 1)}</td>
                        <td>{vague ? '0' : fixed(terms.reduce((a, t) => a + t.vector.y * ys, 0), 1)}</td>
                    </tr>
                </tbody>
            </table>

            {/* The column above already adds up in front of the reader and
                ΣF is its last row, so writing the same sum out again in prose
                said nothing twice. It earns its place only where the sum is
                the WORKING for something else — an acceleration — and there it
                shows where the ma came from. */}
            {fb.mass > 0 && fb.acceleration.length > 0 && (
                <p className="mbench-fbd-eq">
                    ΣF<sub>x</sub> = {line('x')}<br />
                    ΣF<sub>y</sub> = {line('y')}
                </p>
            )}
            <p className="mbench-fbd-sum">
                {adrift ? (
                    /* Standing still is the question a setup asks, and this is
                       the answer being no. Not "ΣF = ma" with an acceleration
                       filled in — nothing here is moving yet — but the plain
                       statement that these forces do not cancel, so it cannot
                       stay where it is. */
                    <>
                        ΣF = {Math.hypot(adrift.x, adrift.y).toFixed(1)} N, and nothing balances it —
                        {' '}so it cannot stand still. It will move the moment you press ▶.
                    </>
                ) : vague ? (
                    <>
                        It cannot move, so ΣF = 0 — but nothing here decides how the load is shared,
                        {' '}so {[...new Set(terms.filter(t => t.unknown).map(t => t.symbol))].join(' and ')} have no value yet.
                        {' '}Unpin something and the bench can work them out.
                    </>
                ) : fb.hold ? (
                    <>
                        It cannot move, so ΣF = 0 — {fb.pinned ? 'the pin' : 'whatever holds it'} supplies
                        {' '}{Math.hypot(fb.hold.x, fb.hold.y).toFixed(1)} N.
                    </>
                ) : !fb.alongAxes.length ? (
                    /* A straight run of rope: no mass, no freedom, nothing to
                       write ΣF along. With no mass it can carry no net force
                       at all, which is the entire argument and the reason the
                       two ends are equal — `ΣF = N` with the number missing
                       was what this printed instead. */
                    <>It has no mass, so whatever is left over would be infinite acceleration: the two pulls must cancel exactly.</>
                ) : (
                    <>
                        ΣF = {fb.alongAxes.map(x => x.toFixed(1)).join(', ')} N
                        {fb.mass > 0 && <> = {kg(fb.mass)} × {fb.acceleration.map(x => x.toFixed(2)).join(', ')} m/s²</>}
                    </>
                )}
            </p>
        </div>
    );
}



/**
 * One choice out of two or three, said as a choice.
 *
 * These were rows of independent-looking buttons, and a reader met four of
 * them in one block with no way to tell that "Free / Pinned" was one decision
 * and "Bolted fast / Runs on the beam" another — two pressed at once looked
 * like two settings turned on. Radios carry that in the control itself: the
 * group is visible, exactly one is on, and picking one lets the other go.
 */
/** Is this block's bottom on the floor? Within a centimetre: a block let down
 *  onto it by the solve, or dropped there by a drag, stops a hair above. */
function standsOnFloor(w: World, id: string): boolean {
    const part = partById(w, id);
    if (!part) return false;
    const bottom = part.at.y - mechanics.extentAlong(w, part, { x: 0, y: 1 });
    return bottom - w.room.floor < 0.01;
}

/**
 * Smooth, rough or fixed on the floor, and how rough when rough. Three choices
 * before a number, because "fixed" is not a coefficient — see
 * `PartKind.floorGrip` — and a slider that ran out at 1.00 could never say it.
 */
function FloorGrip({ legend, value, onChange }: {
    legend: string;
    value: number | 'fixed';
    onChange: (grip: number | 'fixed') => void;
}): React.JSX.Element {
    // What "rough" goes back to after a trip through smooth or fixed.
    const [rough, setRough] = useState(typeof value === 'number' && value > 0 ? value : 0.4);
    return (
        <>
            <Choice
                legend={legend}
                plain
                value={value === 'fixed' ? 'fixed' : value > 0 ? 'rough' : 'smooth'}
                options={[{ id: 'smooth', label: 'Smooth' }, { id: 'rough', label: 'Rough' }, { id: 'fixed', label: 'Fixed' }]}
                onPick={v => onChange(v === 'fixed' ? 'fixed' : v === 'rough' ? rough : 0)}
            />
            {typeof value === 'number' && value > 0 && (
                <Slider
                    label="Friction" unit="μ" min={0.05} max={1.5} step={0.05} value={value}
                    onChange={v => { setRough(v); onChange(v); }}
                />
            )}
        </>
    );
}

function Choice<T extends string>({ legend, value, options, onPick, plain }: {
    legend: string;
    value: T;
    options: { id: T; label: string }[];
    onPick: (id: T) => void;
    /** No box: the name at the left and the options beside it, in the same
     *  row style as the sliders it sits among. */
    plain?: boolean;
}): React.JSX.Element {
    const name = useId();
    const choices = options.map(o => (
        <label key={o.id} data-on={o.id === value || undefined}>
            <input type="radio" name={name} checked={o.id === value} onChange={() => onPick(o.id)} />
            <span>{o.label}</span>
        </label>
    ));
    if (plain) {
        return (
            <div className="mbench-choice is-plain" role="radiogroup" aria-label={legend}>
                <span className="mbench-choice-name">{legend}</span>
                {choices}
            </div>
        );
    }
    return (
        <fieldset className="mbench-choice">
            <legend>{legend}</legend>
            {choices}
        </fieldset>
    );
}

/**
 * The body in a free-body sketch, drawn as the thing it is.
 *
 * Every part came out as the same rounded blue square, so a diagram of a rope
 * end, a wedge and a weight were three identical pictures — and the one thing
 * a free-body diagram has to establish first is WHICH body has been cut out of
 * the bench. These are outlines, not portraits: enough to recognise, drawn at
 * the same size whatever the part's real dimensions, because the sketch is
 * about forces and not about scale.
 */
function Body({ kind, along }: { kind?: string; along?: { x: number; y: number } }): React.JSX.Element {
    const c = 'mbench-fbd-body';
    switch (kind) {
        case 'sheave':
            return <><circle cx={0} cy={0} r={13} className={c} /><circle cx={0} cy={0} r={3} className="mbench-fbd-axle" /></>;
        case 'anchor':
            // A plate bolted to the beam, with the hook hanging off it.
            return <><rect x={-13} y={-13} width={26} height={6} rx="1.5" className={c} /><path d="M0 -7 v8 a5.5 5.5 0 1 0 -5.5 5.5" className={c} fill="none" /></>;
        case 'ropeEnd':
            // A knot: the rope stops here and there is nothing else to draw.
            return <><circle cx={0} cy={0} r={6} className={c} /><path d="M0 6 v7" className={c} fill="none" /></>;
        case 'springBalance':
            return <><rect x={-7} y={-13} width={14} height={26} rx="3" className={c} /><path d="M-4 -4 h8 M-4 2 h8" className={c} fill="none" /></>;
        case 'table':
            return <path d="M-14 -6 h28 M-10 -6 v14 M10 -6 v14" className={c} fill="none" />;
        case 'rope': {
            // A straight run and nothing else — which is the point of cutting
            // the rope at the wheels rather than drawing it as one bent body.
            // And drawn ALONG the run: always upright, a run up a slope was a
            // vertical tick with its two pulls slanting off it, which reads as
            // a rope being pulled sideways.
            const n = along ? Math.hypot(along.x, along.y) : 0;
            const u = n > 1e-9 ? { x: (along as { x: number }).x / n, y: (along as { y: number }).y / n } : { x: 0, y: 1 };
            return (
                <path
                    d={`M${-13 * u.x} ${-13 * u.y} L${13 * u.x} ${13 * u.y}`}
                    className={c} fill="none" strokeWidth={3} strokeLinecap="round"
                />
            );
        }
        case 'incline':
            return <path d="M-14 10 H14 L14 -10 Z" className={c} />;
        default:
            return <rect x={-13} y={-13} width={26} height={26} rx="3" className={c} />;
    }
}

/**
 * A force's name cut down to what fits beside its arrow.
 *
 * The table below the sketch has room for the whole sentence and is where a
 * reader goes for it. The sketch has a box a hundred units wide, and "pull
 * from the rope, round P₁" ran off both sides of it and came back as "rom the
 * rope, roun" — which names nothing at all.
 */
function onArrow(label: string): string {
    const bare = label.replace(/^(pull|push|force)\s+/, '');
    // A clause after a comma is the part that identifies it: "…, round P₁".
    const tail = bare.slice(bare.lastIndexOf(',') + 1).trim();
    // Then the joining words, which carry nothing beside an arrow that is
    // already pointing: "bolt from P₁" is "bolt P₁", "the pin" is "pin".
    return tail.split(' ').filter(word => !/^(from|the|of|it|a|on)$/.test(word)).join(' ') || tail;
}

/**
 * One component of a force whose size nobody knows: `−T`, `0.50 T`, `0.0`.
 *
 * The direction IS known — it is the rope's, read off the geometry — so the
 * component is a fixed multiple of the unknown rather than another unknown.
 * Writing `?` in both columns would discard exactly the half of the answer the
 * arrangement already settles.
 */
/**
 * A row of the parts list: what each part is called and what KIND of part it
 * is — "P₁ (Pulley)" — and beside it the one or two facts that make it this
 * part and not another of its kind: a pulley's radius, a block's mass.
 *
 * The facts are the kind's own `keyFacts`, the same ones the selected part's
 * heading shows, so the list and the heading cannot describe a part two ways.
 * A kind whose facts do not mention its mass still has one worth reading when
 * it is not zero, so it is added. A body bolted together from several parts
 * lists each of them.
 */
function partRow(world: M.World, partIds: string[]): { names: [string, string][]; keys: string[] } {
    const parts = partIds.map(id => partById(world, id)).filter((q): q is NonNullable<typeof q> => !!q);
    return {
        names: parts.map(q => [defOf(q).name(q as never, world), `(${defOf(q).label})`]),
        keys: parts.map(q => {
            const own = defOf(q).keyFacts?.(q as never, world) ?? [];
            const mass = defOf(q).mass(q as never);
            const facts = mass > 0 && !own.some(f => f.endsWith(' kg')) ? [...own, `${+mass.toFixed(2)} kg`] : own;
            return facts.length ? facts.join(' · ') : '—';
        }),
    };
}

/** One line per part, so a bolted group reads as its parts and each part's
 *  property sits on the same line as its name. */
const lines = (xs: string[]): React.JSX.Element => <>{xs.map((x, i) => <div key={i}>{x}</div>)}</>;

/** A part's name and its kind. On a narrow rail the kind drops under the name
 *  rather than pushing Speed and Accel. off the side — and it only ever breaks
 *  there, never inside "m₁" or inside "(Mass block)". */
const named = (xs: [string, string][]): React.JSX.Element => (
    <>{xs.map(([n, kind], i) => <div key={i}><span className="mbench-nw">{n}</span> <span className="mbench-nw">{kind}</span></div>)}</>
);

function times(component: number, symbol: string): string {
    const size = Math.abs(component).toFixed(2);
    if (Number(size) === 0) return '0.0';
    if (Math.abs(Math.abs(component) - 1) < 1e-9) return component < 0 ? `−${symbol}` : symbol;
    return `${component < 0 ? '−' : ''}${size} ${symbol}`;
}

/** A short letter for a force nobody has worked out: T for a rope, N, f, R. */
function symbolOf(f: M.BodyForce): string {
    if (f.kind === 'tension') return 'T';
    if (f.kind === 'friction') return 'f';
    if (f.kind === 'normal') return 'N';
    return 'F';
}

/**
 * One PART on its own, from the solved system.
 *
 * Every force here is a variable the solve found or the part's own weight, and
 * ΣF is the residual the solver drove to zero — so what a reader checks and
 * what the bench did are the same thing. There is no second derivation to
 * disagree with the first.
 */

/**
 * The unknowns as a reader reads them: one row per VECTOR.
 *
 * "Where does P₁ stand" has one answer with two numbers in it, and listing
 * `x of P₁` and `y of P₁` on separate rows makes it two facts with the pairing
 * left for the reader to notice. The engine still counts them as two scalar
 * unknowns — it must, because each has its own equation and that is what makes
 * the system square — so the COUNT above this table is unchanged. Only the
 * reading is.
 */
function vectors(us: M.Unknown[], ys = 1, floor = 0, ceiling = 0): { id: string; label: string; kind: string; text: string }[] {
    const out: { id: string; label: string; kind: string; text: string }[] = [];
    const seen = new Set<string>();
    /* Counted the way the ruler counts. The engine's y runs up; read UP, a
       position is its height above the floor, and read DOWN it is its depth
       below the ceiling and a force's y component changes sign. */
    const shown = (u: M.Unknown): number => (u.axis !== 'y'
        ? u.value
        : ys > 0
            ? (u.unit === 'm' ? u.value - floor : u.value)
            : (u.unit === 'm' ? ceiling - u.value : -u.value));
    us.forEach(u => {
        if (!u.group) {
            out.push({ id: u.id, label: u.label, kind: u.kind, text: `${fixed(shown(u), 3)} ${u.unit}` });
            return;
        }
        if (seen.has(u.group)) return;
        seen.add(u.group);
        const mine = us.filter(x => x.group === u.group);
        const x = mine.find(m => m.axis === 'x'), y = mine.find(m => m.axis === 'y');
        out.push({
            id: u.group,
            label: u.label,
            kind: u.kind,
            text: x && y ? `(${fixed(shown(x), 3)}, ${fixed(shown(y), 3)}) ${u.unit}` : `${fixed(shown(u), 3)} ${u.unit}`,
        });
    });
    return out;
}

function PartFBD({ d, ys = 1 }: { d: NonNullable<ReturnType<typeof mechanics.diagramOf>>; ys?: number }): React.JSX.Element {
    /* The box the sketch lives in, and it is not square.
     *
     * It used to be −50..50 both ways with a 26-unit body in the middle, which
     * left half a pane of nothing under a diagram whose arrows mostly go up
     * and down a short way. The body is drawn at 13, arrows reach ~34 past it,
     * and a label sits beyond that — so the height is what those need and no
     * more. */
    /* Wider than it is tall: labels are written beside the arrows, and a
       name like "m₁ against P₁" runs further sideways than any arrow does. */
    const W = 118, TOP = 72, BOT = 72;
    const biggest = Math.max(1e-6, ...d.forces.map(f => Math.hypot(f.vector.x, f.vector.y)));
    /* The arrows carry the reading, so they get the room. Labels no longer sit
       past the tips (see below), which is what the last 10 units of the box
       were being kept for. */
    /* Every arrow the SAME length: a direction, not a size. Scaled to the
       force, a 9.8 N pin beside a 49 N weight was a stub the reader could not
       find; the sizes are in the table under it, where they can be read. A
       force too small to matter (under a thousandth of the largest) is not
       drawn at all. */
    const ARROW = 46;
    const sized = (v: { x: number; y: number }): { dx: number; dy: number } => {
        const n = Math.hypot(v.x, v.y);
        // The sketch is an SVG, y down; the bench's forces are y up.
        return n < biggest * 1e-3 ? { dx: 0, dy: 0 } : { dx: (v.x / n) * ARROW, dy: (-v.y / n) * ARROW };
    };
    /* Metres to sketch units, so an offset lands where it belongs ON the body:
       a rope's grip on a wheel of radius r is drawn on the drawn rim. */
    const grip = 13 / Math.max(1e-6, d.reach);

    /* An UNKNOWN force is still a force, and it is drawn and listed with its
       value left blank. A diagram that dropped the terms it had no number for
       and then printed the total was the one thing this pane must never do:
       a weight with a rope tied to it was shown holding nothing but its own
       weight against its pin, adding to exactly zero. */
    const vague = d.forces.filter(f => f.unknown).map(f => f.label);
    return (
        <div className="mbench-fbd">
            {/* Its name and the facts about it — how much it weighs, what it
                is free to do. It used to read "P₁ on its own", which is the
                textbook's phrase for isolating a free body and tells a reader
                nothing they can check. */}
            <h3>{d.name} <em>{d.facts.join(' · ')}</em></h3>
            <svg
                viewBox={`${-W} ${-TOP} ${W * 2} ${TOP + BOT}`}
                /* One unit to one pixel, so a 13-unit label is 13px on screen
                   whatever width the pane is (narrower screens scale it down). */
                style={{ width: W * 2, maxWidth: '100%', height: 'auto' }}
                className="mbench-fbd-svg"
                role="img"
                aria-label={`Free-body diagram for ${d.name}`}
            >
                <Body kind={d.shape} />
                {(() => {
                    const drawn = d.forces.map(f => {
                        const { dx, dy } = sized(f.vector);
                        return f.unknown || Math.hypot(dx, dy) < 0.5 ? null : { ax: f.at.x * grip, ay: -f.at.y * grip, dx, dy };
                    });
                    const spread = spreadParallel(drawn, 18);
                    return d.forces.map((f, i) => {
                    const { dx, dy } = sized(f.vector);
                    if (f.unknown || Math.hypot(dx, dy) < 0.5) return null;
                    const { ox, oy, side } = spread[i];
                    /* FROM WHERE IT ACTS. A rope over a wheel pulls at the rim,
                       and drawn through the middle the sketch shows a wheel
                       with no torque on it — losing the very argument that
                       makes the two tensions equal. */
                    const ax = f.at.x * grip + ox, ay = -f.at.y * grip + oy;
                    /* BESIDE the arrow, not beyond it. A label past the tip is
                       read as part of the arrow's length — it makes a short
                       force look long — and it is the reason the arrows had to
                       stop short of the edge. Alongside the shaft it names the
                       arrow without lengthening it.
                       Which side: away from the body, so two tensions gripping
                       opposite rims of a wheel label outwards instead of both
                       writing across the middle. */
                    const len = Math.hypot(dx, dy);
                    let px = -dy / len, py = dx / len;
                    const away = ax * px + ay * py;
                    /* One of a parallel pair: its own outside edge, which the
                       spread already chose. */
                    if (side) { px *= side; py *= side; }
                    else if (Math.abs(away) > 1e-9 ? away < 0 : px < 0) { px = -px; py = -py; }
                    const anchor = px > 0.3 ? 'start' : px < -0.3 ? 'end' : 'middle';
                    let tx = ax + dx * 0.58 + px * 5;
                    const ty = ay + dy * 0.58 + py * 5;
                    /* Never written across the body. A short arrow on the body's
                       own face put its name at the height of the body, and a
                       name running sideways from there ran over it: at that
                       height it starts clear of the body's side instead. */
                    const BODY = 13, CLEAR = 5;
                    if (Math.abs(ty) < BODY + CLEAR) {
                        if (anchor === 'end') tx = Math.min(tx, -BODY - CLEAR);
                        if (anchor === 'start') tx = Math.max(tx, BODY + CLEAR);
                    }
                    return (
                        <g key={i} className="mbench-fbd-arrow" data-kind={/mg/.test(f.label) ? 'weight' : /bolt|pin|holds/.test(f.label) ? 'normal' : 'tension'}>
                            <line x1={ax} y1={ay} x2={ax + dx} y2={ay + dy} markerEnd="url(#pb-arrow)" />
                            <circle cx={ax} cy={ay} r={1.6} className="mbench-fbd-at" />
                            <text
                                x={tx}
                                y={ty}
                                textAnchor={anchor}
                                dominantBaseline="middle"
                            >
                                {onArrow(f.label)}
                            </text>
                        </g>
                    );
                    });
                })()}
                <defs>
                    <marker id="pb-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                    </marker>
                    <marker id="pb-tick" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                    </marker>
                </defs>
            </svg>
            <table className="mbench-fbdtable">
                <thead><tr><th>Force</th><th>x</th><th>y</th></tr></thead>
                <tbody>
                    {d.forces.map((f, i) => (
                        <tr key={i} data-unknown={f.unknown ? 'yes' : undefined}>
                            <td>{f.label}</td>
                            <td>{f.unknown ? '?' : fixed(f.vector.x, 2)}</td>
                            <td>{f.unknown ? '?' : fixed(f.vector.y * ys, 2)}</td>
                        </tr>
                    ))}
                    <tr className="is-sum">
                        <td>ΣF</td>
                        <td>{d.resolved ? fixed(d.net.x, 2) : '?'}</td>
                        <td>{d.resolved ? fixed(d.net.y * ys, 2) : '?'}</td>
                    </tr>
                </tbody>
            </table>
            {/* The column already adds up in front of the reader and the total
                is the last row of it, so spelling the same sum out again in
                prose said nothing the table had not. It belongs where there IS
                something more to say — beside an acceleration. */}
            {!d.resolved && (
                <p className="mbench-claim">
                    {vague.join(' and ')} {vague.length > 1 ? 'have' : 'has'} no value yet, so this
                    {' '}diagram cannot be added up. The bench has not found an arrangement that
                    {' '}stands still — fix that and every force here gets a number.
                </p>
            )}
        </div>
    );
}

/**
 * One property, as a slider with its number beside it.
 *
 * A slider and not a number box, because the question a reader has is "what
 * happens as this gets bigger" — and dragging it answers that continuously,
 * where typing 4 and then 5 and then 6 answers it three times. The number is
 * still shown, and is still exact, so an answer can be checked against a book.
 */
function Slider({ label, unit, min, max, step, value, note, noteInline, onChange }: {
    label: string;
    unit: string;
    min: number;
    max: number;
    step: number;
    value: number;
    note?: string;
    /** A SHORT note ("Earth", "dead landings") on the label row, just before
     *  the value it describes, rather than on a line of its own under the
     *  track. Long notes read better below, so this is the caller's choice. */
    noteInline?: boolean;
    onChange: (v: number) => void;
}): React.JSX.Element {
    const dp = step < 0.1 ? 2 : step < 1 ? 1 : 0;
    return (
        <label className="mbench-slider">
            <span>{label}</span>
            <b>{note && noteInline && <i className="mbench-slider-note">{note}</i>}{fixed(value, dp)}<u>{unit}</u></b>
            <input
                type="range" min={min} max={max} step={step} value={value}
                onChange={e => onChange(+e.target.value)}
            />
            {note && !noteInline && <em>{note}</em>}
        </label>
    );
}

function RailGroup({ title, children, open: start = true, fixed = false, className = '' }: {
    title: string;
    children: React.ReactNode;
    open?: boolean;
    /** Always open, its head a plain heading — a phone's pane, which scrolls. */
    fixed?: boolean;
    className?: string;
}): React.JSX.Element {
    const [open, setOpen] = useState(start);
    if (fixed) {
        return (
            <section className={`mbench-group ${className}`.trim()} data-open="true">
                <h3 className="mbench-grouphead">{title}</h3>
                <div className="mbench-groupbody">{children}</div>
            </section>
        );
    }
    return (
        <section className={`mbench-group ${className}`.trim()} data-open={open}>
            <button type="button" className="mbench-grouphead" onClick={() => setOpen(o => !o)} aria-expanded={open}>
                {title}<span aria-hidden="true">⌃</span>
            </button>
            {open && <div className="mbench-groupbody">{children}</div>}
        </section>
    );
}
